// dataReceiver.js - APIM data access layer for the RTLS Dashboard frontend
// ─────────────────────────────────────────────────────────────────────────────
// ── Route parameter reference ─────────────────────────────────────────────────
// Every call maps to:
//   GET <APIM_BASE_URL>/dataServe?route=<routeName>&...params
//
// Implemented routes
//   rtls-heatmap                    fetchHeatmap()
//   rtls-spaghetti                  fetchPositionHistory()
//   modules-list                    fetchModulesList()
//   noncompliances                  fetchNoncompliances()
//   noncompliances/fullList         fetchNoncompliancesFullList()
//   noncompliances/detail           fetchNoncompliancesDetail({ batchNum })
//   noncompliances/metric-tasks     fetchNoncompliancesMetricTasks({ metric })
//   modal-weekly-progress           fetchWeeklyProgress()
//   module-metrics                  fetchModuleMetrics({ woNum })
//   activity-module                 fetchActivityModule({ woNum })
//   personnel-list                  fetchPersonnelList()
//   personnel-metrics               fetchPersonnelMetrics({ employeeNumber })
//   homepage-overall                fetchHomepageMetrics()
//   modal-roll-times                fetchModalRollTime()
//   activity-personnel              fetchActivityHistory({ employeeId })
//   goals-management                fetchGoalsManagement()
//   workflow-map                    fetchWorkflowMap({ objectName, jobName, jobNumber?, beginTs, endTs, beginDateTime, endDateTime, samplingPeriod? })
//   rtls-playback-map               fetchPlaybackMap({ objectId, beginTs, endTs, samplingPeriod? })
//   avg-count    fetchAvgCount()
//
//
// ─── Base URL ─────────────────────────────────────────────────────────────────

const APIM_BASE_URL = (
  window.ENV.APIM_BASE_URL || "http://localhost:7071/api"
).replace(/\/+$/, ""); // strip trailing slash

// ─── APIM subscription key (optional) ────────────────────────────────────────

const APIM_SUBSCRIPTION_KEY = window.ENV.APIM_SUBSCRIPTION_KEY ?? "";

// ─── Internal fetch helper ────────────────────────────────────────────────────

/**
 * Perform a GET request against the dataServe Azure Function.
 *
 * @param {string}            routeName  - Value of the "route" query param
 * @param {URLSearchParams}   params     - Additional query params (no "route" key needed)
 * @param {AbortSignal}       [signal]   - Optional AbortController signal for cancellation
 * @returns {Promise<unknown>}           - Parsed JSON response body
 * @throws {DataReceiverError}           - On HTTP error or non-JSON response
 */
async function apimGet(routeName, params, signal) {
  // Prepend the route name to the params that will become the query string
  const fullParams = new URLSearchParams({ route: routeName });
  params.forEach((value, key) => fullParams.set(key, value));

  const url = `${APIM_BASE_URL}/dataServe?${fullParams.toString()}`;

  const headers = {
    Accept: "application/json",
  };

  if (APIM_SUBSCRIPTION_KEY) {
    headers["Ocp-Apim-Subscription-Key"] = APIM_SUBSCRIPTION_KEY;
  }

  let res;
  try {
    res = await fetch(url, { method: "GET", headers, signal });
  } catch (networkErr) {
    // AbortError is a normal cancellation - re-throw as-is so callers can
    // detect it with err.name === 'AbortError'
    if (networkErr.name === "AbortError") throw networkErr;
    throw new DataReceiverError(
      `Network error reaching APIM: ${networkErr.message}`,
      0,
      null,
    );
  }

  // ── Content-type guard ────────────────────────────────────────────────────
  const contentType = res.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    const preview = await res.text().then((t) => t.slice(0, 300));
    throw new DataReceiverError(
      `APIM returned non-JSON for route "${routeName}" ` +
        `(HTTP ${res.status} - ${contentType}). ` +
        `Check REACT_APP_APIM_BASE_URL is correct. Preview: ${preview}`,
      res.status,
      null,
    );
  }

  const json = await res.json();

  if (!res.ok) {
    // The backend serializes errors as { error, message, route }
    const message =
      json?.message ??
      json?.error ??
      `APIM route "${routeName}" returned HTTP ${res.status}`;
    throw new DataReceiverError(message, res.status, json);
  }

  return json;
}

// ─── DataReceiverError ────────────────────────────────────────────────────────

/**
 * Typed error thrown by all dataReceiver functions.
 * Callers can inspect .status and .payload for structured error handling.
 */
export class DataReceiverError extends Error {
  /**
   * @param {string}  message   - Human-readable error message
   * @param {number}  status    - HTTP status code (0 = network failure)
   * @param {unknown} payload   - Raw error payload from the backend (may be null)
   */
  constructor(message, status, payload) {
    super(message);
    this.name = "DataReceiverError";
    this.status = status;
    this.payload = payload;
  }
}

// ─── RTLS - Heatmap ───────────────────────────────────────────────────────────

/**
 * Fetch heatmap grid data from the backend RTLS route.
 *
 * Replaces the direct `/riot-api/heat-map` call in useHeatmap.js.
 * Object ID resolution still happens client-side via window.Riot.Api
 * (the backend has no WebSocket SDK access), so pass already-resolved
 * numeric IDs here.
 *
 * @param {{
 *   locationId?:  number,   - Kinexon location ID (default 1)
 *   beginTs:      number,   - Range start, Unix seconds
 *   endTs:        number,   - Range end, Unix seconds
 *   objectIds?:   number[], - Resolved Kinexon object IDs
 *   labels?:      string[], - Label filter strings
 * }} params
 *
 * @param {AbortSignal} [signal]
 *
 * @returns {Promise<{
 *   data: [number, number, number][],   - [cell_x, cell_y, count] rows
 *   meta: {
 *     cellCount: number,
 *     minCount:  number,
 *     maxCount:  number,
 *   },
 * }>}
 *
 * @throws {DataReceiverError}
 */
export async function fetchHeatmap(
  { locationId = 8, beginTs, endTs, objectIds = [], labels = [] },
  signal,
) {
  if (!beginTs || !endTs) {
    throw new DataReceiverError(
      "fetchHeatmap: 'beginTs' and 'endTs' are required.",
      400,
      null,
    );
  }

  const params = new URLSearchParams({
    location_id: String(locationId),
    begin: String(beginTs),
    end: String(endTs),
  });

  if (objectIds.length > 0) {
    params.set("object_ids", objectIds.join(","));
  }

  if (labels.length > 0) {
    params.set("labels", labels.join(","));
  }

  const json = await apimGet("rtls-heatmap", params, signal);
  return json;
}

// ─── RTLS - Spaghetti / Position History ─────────────────────────────────────

/**
 * Fetch position history (spaghetti map) data from the backend RTLS route.
 *
 * Replaces the direct `/riot-api/position-history` call in useSpaghettiMap.js.
 * Object ID resolution still happens client-side via window.Riot.Api.
 *
 * @param {{
 *   beginTs:         number,   - Range start, Unix seconds
 *   endTs:           number,   - Range end, Unix seconds
 *   objectIds?:      number[], - Resolved Kinexon object IDs
 *   labels?:         string[], - Label filter strings
 *   samplingPeriod?: number,   - Seconds between samples (default 60)
 * }} params
 *
 * @param {AbortSignal} [signal]
 *
 * @returns {Promise<{
 *   data: [number, number, number, number, number][],  - [ts, objectId, x, y, z] rows
 *   meta: {
 *     pointCount:  number,
 *     trackCount:  number,
 *     objectIds:   number[],
 *   },
 * }>}
 *
 * @throws {DataReceiverError}
 */
export async function fetchPositionHistory(
  { beginTs, endTs, objectIds = [], labels = [], samplingPeriod = 60 },
  signal,
) {
  if (!beginTs || !endTs) {
    throw new DataReceiverError(
      "fetchPositionHistory: 'beginTs' and 'endTs' are required.",
      400,
      null,
    );
  }

  const params = new URLSearchParams({
    begin: String(beginTs),
    end: String(endTs),
    sampling_period: String(samplingPeriod),
  });

  if (objectIds.length > 0) {
    params.set("object_ids", objectIds.join(","));
  }

  if (labels.length > 0) {
    params.set("labels", labels.join(","));
  }

  const json = await apimGet("rtls-spaghetti", params, signal);
  return json;
}

// ─── RTLS - Playback Map ──────────────────────────────────────────────────────

/**
 * Fetch dense position history for a single object for playback animation.
 *
 * Replaces any direct `/riot-api/position-history` call for the playback
 * feature. Object ID resolution still happens client-side via window.Riot.Api
 * (the backend has no WebSocket SDK access), so pass an already-resolved
 * numeric ID here.
 *
 * Uses a fine sampling_period (default 1s) to produce smooth animation frames.
 * For long time ranges (e.g. a full 8-hour shift at 1s = ~28 800 frames) the
 * caller should increase samplingPeriod to cap total frame count to a
 * reasonable number before calling this function.
 *
 * The backend sorts rows ascending by timestamp before responding, so the
 * returned `frames` array is always in chronological order.
 *
 * @param {{
 *   objectId:        number,   - Single resolved Kinexon object ID (required)
 *   beginTs:         number,   - Range start, Unix seconds (required)
 *   endTs:           number,   - Range end, Unix seconds (required)
 *   samplingPeriod?: number,   - Seconds between position samples (default 1)
 * }} params
 *
 * @param {AbortSignal} [signal]
 *
 * @returns {Promise<{
 *   frames: [number, number, number, number, number][],  - [ts, objectId, x, y, z] rows (ascending)
 *   meta: {
 *     frameCount:     number,   - Total position rows returned
 *     durationMs:     number,   - Wall-clock span of the track in milliseconds
 *     samplingPeriod: number,   - Echo of the sampling_period used
 *     objectId:       number,   - Echo of the resolved object_id
 *   },
 * }>}
 *
 * @throws {DataReceiverError}
 */
export async function fetchPlaybackMap(
  { objectId, beginTs, endTs, samplingPeriod = 1 },
  signal,
) {
  if (objectId == null || !Number.isFinite(Number(objectId))) {
    throw new DataReceiverError(
      "fetchPlaybackMap: 'objectId' is required and must be a valid integer.",
      400,
      null,
    );
  }

  if (!beginTs || !endTs) {
    throw new DataReceiverError(
      "fetchPlaybackMap: 'beginTs' and 'endTs' are required (Unix seconds).",
      400,
      null,
    );
  }

  if (Number(beginTs) >= Number(endTs)) {
    throw new DataReceiverError(
      "fetchPlaybackMap: 'beginTs' must be earlier than 'endTs'.",
      400,
      null,
    );
  }

  const resolvedSamplingPeriod =
    Number.isFinite(Number(samplingPeriod)) && Number(samplingPeriod) > 0
      ? Number(samplingPeriod)
      : 1;

  const params = new URLSearchParams({
    begin: String(beginTs),
    end: String(endTs),
    object_id: String(objectId),
    sampling_period: String(resolvedSamplingPeriod),
  });

  const json = await apimGet("rtls-playback-map", params, signal);

  // ── Normalise response ─────────────────────────────────────────────────────
  //
  // Defensively coerce every field so usePlaybackMap.js never has to guard
  // against a partial 200. The backend guarantees ascending sort on timestamp,
  // but we preserve that guarantee here by re-sorting just in case.
  const rawData = Array.isArray(json?.data) ? json.data : [];

  const frames = rawData
    .map((row) => [
      Number(row[0]), // ts        (Unix seconds, may be fractional)
      Number(row[1]), // object_id
      Number(row[2]), // x
      Number(row[3]), // y
      Number(row[4]), // z
    ])
    .sort((a, b) => a[0] - b[0]); // ascending by timestamp

  const meta = {
    frameCount: Number(json?.meta?.frameCount ?? frames.length),
    durationMs: Number(json?.meta?.durationMs ?? 0),
    samplingPeriod: Number(
      json?.meta?.samplingPeriod ?? resolvedSamplingPeriod,
    ),
    objectId: Number(json?.meta?.objectId ?? objectId),
  };

  return { frames, meta };
}

// ─── Modules List ─────────────────────────────────────────────────────────────

/**
 * Fetch the list of modules currently active on the production line.
 *
 * The backend returns one of three shapes - all are normalised to a plain array:
 *   { rows: [...], count: N }   ← current production shape (confirmed via HAR)
 *   { data: [...] }             ← alternative envelope
 *   [...]                       ← bare array
 *
 * @param {AbortSignal} [signal]
 *
 * @returns {Promise<Array<{
 *   BatchNum:             string,  - Batch identifier
 *   Property:            string,  - Property / project name
 *   scheduledOnlineDate: string,  - Planned online date (YYYY-MM-DD)
 *   WoNum:               string,  - Work order number
 *   ItemNum:             string,  - Item / SKU number
 *   Sqft:                number,  - Square footage
 *   actualOnlineDate:    string,  - Actual online datetime (ISO 8601)
 * }>>}
 *
 * @throws {DataReceiverError}
 */
export async function fetchModulesList(signal) {
  const json = await apimGet("modules-list", new URLSearchParams(), signal);

  // Normalise all known envelope shapes to a plain array.
  // Priority: rows (current) → data (legacy) → bare array → empty fallback.
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.rows)) return json.rows;
  if (Array.isArray(json?.data)) return json.data;

  return [];
}

// ─── Noncompliances - Seed (top counts + online mod list) ─────────────────────

/**
 * Fetch the seed data for the Noncompliances modal.
 *
 * Called once on modal open. Returns three job-count integers used by the top
 * MetricBox row, plus the onlineMods array that populates the default left-hand
 * module list (mods currently on the production line).
 *
 * Response shape (200):
 *   {
 *     jobsCC:     number,   - jobs with ≥1 Complete step today
 *     jobsUE:     number,   - jobs with an NC step whose reason is
 *                              "Moving Mod to the Next Station" today
 *     jobsNC:     number,   - jobs with an NC step (other reason) today
 *     onlineMods: Array<{
 *       BatchNum:             string | null,
 *       WoNum:                string | null,
 *       ItemNum:              string | null,
 *       Property:             string | null,
 *       scheduledOnlineDate:  string | null,  - "YYYY-MM-DD"
 *       actualOnlineDate:     string | null,  - ISO datetime
 *       Sqft:                 number | null,
 *     }>
 *   }
 *
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ jobsCC: number, jobsUE: number, jobsNC: number, onlineMods: Array }>}
 * @throws {DataReceiverError}
 */
export async function fetchNoncompliances(signal) {
  const json = await apimGet("noncompliances", new URLSearchParams(), signal);
  return {
    jobsCC: Number(json?.jobsCC ?? 0),
    jobsUE: Number(json?.jobsUE ?? 0),
    jobsNC: Number(json?.jobsNC ?? 0),
    onlineMods: Array.isArray(json?.onlineMods) ? json.onlineMods : [],
  };
}

// ─── Noncompliances - Full List (search source) ───────────────────────────────

/**
 * Fetch every manufactured item in the database (excluding sentinel WoNum='1').
 *
 * This list is the source for the search-mode left panel.  Filtering by
 * woNum / batchNum / itemNum happens client-side in noncompliances.js after
 * the result is cached in the hook.
 *
 * Response shape (200):
 *   {
 *     rows: Array<{
 *       WoNum:    string | null,
 *       ItemNum:  string | null,
 *       BatchNum: string | null,
 *       CreatedAt: string | null,   - ISO-8601 datetime
 *     }>,
 *     count: number
 *   }
 *
 * @param {AbortSignal} [signal]
 * @returns {Promise<Array<{ WoNum: string, ItemNum: string, BatchNum: string, CreatedAt: string }>>}
 * @throws {DataReceiverError}
 */
export async function fetchNoncompliancesFullList(signal) {
  const json = await apimGet(
    "noncompliances/fullList",
    new URLSearchParams(),
    signal,
  );

  // Normalise envelope - backend currently returns { rows, count }
  if (Array.isArray(json?.rows)) return json.rows;
  if (Array.isArray(json)) return json;
  return [];
}

// ─── Noncompliances - Detail (single mod) ────────────────────────────────────

/**
 * Fetch the full station-by-station breakdown for a single manufactured item.
 *
 * Response shape (200):
 *   {
 *     header: {
 *       woNum:      string | null,
 *       batchNum:   string | null,
 *       itemNum:    string | null,
 *       createdAt:  string | null,   - ISO-8601
 *       lastSeenAt: string | null,   - ISO-8601
 *     },
 *     stations: Array<{
 *       stationId:     number,
 *       stationNumber: number,
 *       stationName:   string,
 *       summary: {
 *         timeInStation:       string,   - "Xh Ym" human-readable
 *         completionRate:      number,   - 0-100
 *         nonconformanceCount: number,
 *         correctedCount:      number,
 *       },
 *       tasks: Array<{
 *         taskId:          string,
 *         taskName:        string,
 *         taskDescription: string | null,
 *         taskGroupName:   string | null,
 *         zoneName:        string | null,
 *         steps: Array<{
 *           stepName:        string,
 *           status:          string,   - "Complete" | "Non-Conformance" | "Corrected" | …
 *           updatedAt:       string,   - ISO-8601
 *           updatedBy:       string | null,
 *           stepComment:     string | null,
 *           timezone:        string | null,
 *           reasons:         string[],
 *           images:          string[],
 *           hasVersionHistory: boolean,
 *           versionHistory:  Array<{ status, updatedAt, updatedBy, stepComment, timezone, reasons, images }>,
 *         }>
 *       }>
 *     }>,
 *     _meta: { stationCount: number, totalSteps: number, payloadNote: string }
 *   }
 *
 * @param {{ batchNum: string }} params
 * @param {AbortSignal} [signal]
 * @returns {Promise<object>}  Full detail response as described above.
 * @throws {DataReceiverError}
 */
export async function fetchNoncompliancesDetail({ batchNum }, signal) {
  if (!batchNum) {
    throw new DataReceiverError(
      "fetchNoncompliancesDetail: 'batchNum' is required.",
      400,
      null,
    );
  }

  const params = new URLSearchParams({ batchNum });
  const json = await apimGet("noncompliances/detail", params, signal);
  return json;
}

// ─── Noncompliances - Metric Tasks (per-metric item drill-down) ───────────────

/**
 * Fetch per-item task details for one of the three header metric boxes in the
 * Noncompliances modal. All results are scoped to UTC calendar-today, matching
 * the seed counts returned by fetchNoncompliances() exactly.
 *
 * Consumed by: useNoncompliances.js → metric-box drill-down modals
 *
 * @param {{ metric: 'completed' | 'unevaluated' | 'nonconformed' }} params
 * @param {AbortSignal} [signal]
 *
 * @returns {Promise<{
 *   metric:  'completed' | 'unevaluated' | 'nonconformed',
 *   count:   number,
 *   items: Array<{
 *     manufacturingItemId: number,
 *     woNum:               string | null,
 *     itemNum:             string | null,
 *     batchNum:            string | null,
 *     steps: Array<{
 *       stationName:     string | null,
 *       taskDescription: string | null,
 *       stepName:        string | null,
 *       stepStatus:      string | null,
 *       updatedAt:       string | null,   - ISO-8601
 *       updatedBy:       string | null,
 *       stepComment:     string | null,
 *       reasons:         string[],        - [] for completed steps
 *     }>
 *   }>
 * }>}
 *
 * @throws {DataReceiverError}
 */
export async function fetchNoncompliancesMetricTasks({ metric }, signal) {
  const VALID_METRICS = new Set(["completed", "unevaluated", "nonconformed"]);

  if (!metric) {
    throw new DataReceiverError(
      "fetchNoncompliancesMetricTasks: 'metric' is required.",
      400,
      null,
    );
  }
  if (!VALID_METRICS.has(metric)) {
    throw new DataReceiverError(
      `fetchNoncompliancesMetricTasks: invalid metric "${metric}". ` +
        "Accepted values: completed, unevaluated, nonconformed.",
      400,
      null,
    );
  }

  const params = new URLSearchParams({ metric });
  const json = await apimGet("noncompliances/metric-tasks", params, signal);

  return {
    metric: json?.metric ?? metric,
    count: Number(json?.count ?? 0),
    items: Array.isArray(json?.items) ? json.items : [],
  };
}

// ─── Weekly Progress Modal ────────────────────────────────────────────────────

/**
 * Fetch the 4 weekly production progress metrics for the homepage modal.
 *
 * No parameters required - the backend derives the current Monday–Sunday
 * business week in UTC automatically.
 *
 * Consumed by: useWeeklyProgress.js → Weekly Production Progress modal
 *
 * @param {AbortSignal} [signal]
 *
 * @returns {Promise<{
 *   weeklyUnitsCompleted:  { actual: number, target: number, percentage: number },
 *   weeklySqftCompleted:   { actual: number, target: number, percentage: number },
 *   onTimeCompletion:      { actual: number, target: number, percentage: number },
 *   weeklyQualityPassRate: { actual: number, target: number, percentage: number },
 *   meta: { weekStart: string, weekEnd: string }   - "YYYY-MM-DD"
 * }>}
 *
 * Notes on metric shapes:
 *   - actual for rate metrics (onTimeCompletion, weeklyQualityPassRate) is a
 *     0–1 decimal ratio (e.g. 0.92 = 92 %).
 *   - actual for count metrics (weeklyUnitsCompleted, weeklySqftCompleted) is
 *     the raw integer / decimal aggregate.
 *   - percentage = (actual / target) × 100, rounded to 2 decimal places.
 *     Can exceed 100 when actual surpasses target.
 *
 * @throws {DataReceiverError}
 */
export async function fetchWeeklyProgress(signal) {
  const json = await apimGet(
    "modal-weekly-progress",
    new URLSearchParams(),
    signal,
  );

  // Normalise each metric sub-object defensively - guards against partial
  // backend responses while keeping all four fields individually accessible.
  function normMetric(raw) {
    return {
      actual: Number(raw?.actual ?? 0),
      target: Number(raw?.target ?? 0),
      percentage: Number(raw?.percentage ?? 0),
    };
  }

  return {
    weeklyUnitsCompleted: normMetric(json?.weeklyUnitsCompleted),
    weeklySqftCompleted: normMetric(json?.weeklySqftCompleted),
    onTimeCompletion: normMetric(json?.onTimeCompletion),
    weeklyQualityPassRate: normMetric(json?.weeklyQualityPassRate),
    meta: {
      weekStart: json?.meta?.weekStart ?? null,
      weekEnd: json?.meta?.weekEnd ?? null,
    },
  };
}

// ─── Active Personnel Modal ───────────────────────────────────────────────────

/**
 * Fetch the active-personnel list for the homepage modal.
 *
 * The backend joins Azure SQL (RTLS_HomepageOverallQuery3) with the RTLS
 * /objects and /position-history endpoints to derive a location status for
 * each employee. Only employees present in the SQL table are returned.
 *
 * Response shape (200):
 *   {
 *     personnel: Array<{
 *       employeeName:   string,                                           - "First Last"
 *       currentJob:     string | null,                                    - Description column
 *       lastUpdated:    string | null,                                    - ISO-8601, null when no recent position
 *       locationStatus: "Active" | "No Active Position" | "No Locations Found"
 *     }>,
 *     count: number
 *   }
 *
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ personnel: Array, count: number }>}
 * @throws {DataReceiverError}
 */
export async function fetchActivePersonnel(signal) {
  const json = await apimGet(
    "modal-active-personnel",
    new URLSearchParams(),
    signal,
  );
  return {
    personnel: Array.isArray(json?.personnel) ? json.personnel : [],
    count: Number(json?.count ?? 0),
  };
}

// ─── Module Metrics ───────────────────────────────────────────────────────────

/**
 * Fetch combined module info and metrics for a single work order.
 *
 * Consumed by: useModuleMetrics.js → ModuleMetricsPage
 *
 * The backend fires three SQL queries in parallel and returns a single
 * normalised payload. All date strings are ISO-8601; timeOnLine is a
 * human-readable "Xh Ym" string computed server-side.
 *
 * @param {{ woNum: string }} params
 * @param {AbortSignal} [signal]
 *
 * @returns {Promise<{
 *   moduleInfo: {
 *     woNum:                string | null,
 *     itemNum:              string | null,
 *     batchNum:             string | null,
 *     scheduledOnlineDate:  string | null,   - "YYYY-MM-DD"
 *     actualOnlineDate:     string | null,   - ISO-8601 datetime
 *     scheduledOfflineDate: string | null,   - ISO-8601 datetime
 *     sqft:                 number | null,
 *     property:             string | null,
 *   },
 *   moduleMetrics: {
 *     currentStation:   number | null,
 *     jobsRedone:       number,
 *     timeOnLine:       string | null,       - "Xh Ym" | "< 1 min" | null
 *     totalMannedHours: number,
 *     completionRate:   number,              - 0–100 decimal (e.g. 87.50)
 *   }
 * }>}
 *
 * @throws {DataReceiverError}
 */
export async function fetchModuleMetrics({ woNum }, signal) {
  if (!woNum?.trim()) {
    throw new DataReceiverError(
      "fetchModuleMetrics: 'woNum' is required.",
      400,
      null,
    );
  }

  const params = new URLSearchParams({ woNum: woNum.trim() });
  const json = await apimGet("module-metrics", params, signal);

  // Normalise both sub-objects defensively - guards against partial responses.
  const info = json?.moduleInfo ?? {};
  const metrics = json?.moduleMetrics ?? {};

  return {
    moduleInfo: {
      woNum: info.woNum ?? null,
      itemNum: info.itemNum ?? null,
      batchNum: info.batchNum ?? null,
      scheduledOnlineDate: info.scheduledOnlineDate ?? null,
      actualOnlineDate: info.actualOnlineDate ?? null,
      scheduledOfflineDate: info.scheduledOfflineDate ?? null,
      sqft: info.sqft != null ? Number(info.sqft) : null,
      property: info.property ?? null,
    },
    moduleMetrics: {
      currentStation:
        metrics.currentStation != null ? Number(metrics.currentStation) : null,
      jobsRedone: Number(metrics.jobsRedone ?? 0),
      timeOnLine: metrics.timeOnLine ?? null,
      totalMannedHours: Number(metrics.totalMannedHours ?? 0),
      completionRate: Number(metrics.completionRate ?? 0),
    },
  };
}

// ─── Activity Module ─────────────────────────────────────────────────────────

/**
 * Fetch the full activity history for a single work order.
 *
 * Consumed by: ActivityHistory modal component → ModuleMetricsPage
 *
 * Joins RTLS_ModuleMetricsActivityHistory (job records) with
 * RTLS_ModuleMetricsActivityHistory2 (worker assignments) on JobID.
 * The backend aggregates worker rows per job server-side, so every job
 * entry carries a pre-parsed workers array - no client-side joining needed.
 *
 * @param {{ woNum: string }} params
 * @param {AbortSignal} [signal]
 *
 * @returns {Promise<{
 *   jobs: Array<{
 *     jobId:           string | null,
 *     operationNumber: number | null,
 *     jobStatus:       string | null,
 *     startDate:       string | null,   - ISO-8601 datetime (RealizedStartDate)
 *     startTime:       string | null,   - raw string      (RealizedStartTime)
 *     endDate:         string | null,   - ISO-8601 datetime (RealizedEndDate)
 *     endTime:         string | null,   - raw string      (RealizedEndTime)
 *     woNum:           string | null,   - ProductionOrder echo
 *     hours:           number | null,
 *     workers: Array<{
 *       employeeId:   string | null,
 *       employeeName: string | null,
 *     }>
 *   }>,
 *   count: number
 * }>}
 *
 * @throws {DataReceiverError}
 */
export async function fetchActivityModule({ woNum }, signal) {
  if (!woNum?.trim()) {
    throw new DataReceiverError(
      "fetchActivityModule: 'woNum' is required.",
      400,
      null,
    );
  }

  const params = new URLSearchParams({ woNum: woNum.trim() });
  const json = await apimGet("activity-module", params, signal);

  // Normalise the jobs array defensively - guards against partial responses.
  const rawJobs = Array.isArray(json?.jobs) ? json.jobs : [];

  const jobs = rawJobs.map((job) => ({
    jobId: job.jobId ?? null,
    operationNumber:
      job.operationNumber != null ? Number(job.operationNumber) : null,
    jobStatus: job.jobStatus ?? null,
    startDate: job.startDate ?? null,
    startTime: job.startTime ?? null,
    endDate: job.endDate ?? null,
    endTime: job.endTime ?? null,
    woNum: job.woNum ?? null,
    hours: job.hours != null ? Number(job.hours) : null,
    workers: Array.isArray(job.workers)
      ? job.workers.map((w) => ({
          employeeId: w.employeeId ?? null,
          employeeName: w.employeeName ?? null,
        }))
      : [],
  }));

  return { jobs, count: jobs.length };
}

// ─── Personnel List ───────────────────────────────────────────────────────────

/**
 * Fetch every employee currently known to the RTLS system.
 *
 * Consumed by: usePersonnelData.js → PersonnelMetricsPage search bar
 *
 * The backend queries RTLS /objects?labels=Employee and returns one entry per
 * employee. No parameters required - the full roster is always returned and
 * filtered client-side.
 *
 * Response shape (200):
 *   {
 *     personnel: Array<{
 *       name:           string | null,   - full name, e.g. "Seth Ornelas"
 *       objId:          number | null,   - RTLS object ID
 *       employeeNumber: string | null,   - maps to RTLS properties.EmployeeID
 *       department:     string | null,   - first non-"Employee" label, or null
 *     }>,
 *     count: number
 *   }
 *
 * @param {AbortSignal} [signal]
 *
 * @returns {Promise<Array<{
 *   name:           string | null,
 *   objId:          number | null,
 *   employeeNumber: string | null,
 *   department:     string | null,
 * }>>}
 *
 * @throws {DataReceiverError}
 */
export async function fetchPersonnelList(signal) {
  const json = await apimGet("personnel-list", new URLSearchParams(), signal);

  // Normalise the personnel array defensively - guards against partial responses.
  const rawPersonnel = Array.isArray(json?.personnel) ? json.personnel : [];

  return rawPersonnel.map((p) => ({
    name: p.name ?? null,
    objId: p.objId ?? null,
    employeeNumber: p.employeeNumber ?? null,
    department: p.department ?? null,
  }));
}

// ─── Personnel Metrics ────────────────────────────────────────────────────────

/**
 * Fetch a full metrics snapshot for a single employee.
 *
 * Consumed by: usePersonnelData.js → PersonnelMetricsPage metric cards
 *
 * The backend resolves identity via RTLS, then fans out to SQL (today's shift
 * rows), a single position record, and position-history for movement distance -
 * all parallelised server-side. Partial failures (e.g. RTLS position unavailable)
 * are non-fatal; affected fields are returned as null / 0.
 *
 * @param {{ employeeNumber: string }} params
 * @param {AbortSignal} [signal]
 *
 * @returns {Promise<{
 *   fullName:        string | null,   - "First Last"
 *   employeeNumber:  string | null,   - echoed back
 *   department:      string | null,   - from RTLS labels
 *   currentZone:     string | null,   - RTLS properties.currentZone
 *   currentJob:      string | null,   - active job description (EndTime = "12:00 AM")
 *   lastUpdatedTime: string | null,   - ISO-8601 from RTLS lastValidPos
 *   tagBatteryLevel: number | null,   - batteryInfo.batteryVoltage
 *   timeInIndirect:  number,          - minutes in indirect jobs today
 *   totalMovement:   number,          - metres walked this shift
 * }>}
 *
 * @throws {DataReceiverError}
 */
export async function fetchPersonnelMetrics({ employeeNumber }, signal) {
  if (!employeeNumber?.trim()) {
    throw new DataReceiverError(
      "fetchPersonnelMetrics: 'employeeNumber' is required.",
      400,
      null,
    );
  }

  const params = new URLSearchParams({
    employeeNumber: employeeNumber.trim(),
  });
  const json = await apimGet("personnel-metrics", params, signal);

  // Normalise defensively - guards against partial backend responses.
  return {
    fullName: json?.fullName ?? null,
    employeeNumber: json?.employeeNumber ?? null,
    department: json?.department ?? null,
    currentZone: json?.currentZone ?? null,
    currentJob: json?.currentJob ?? null,
    lastUpdatedTime: json?.lastUpdatedTime ?? null,
    tagBatteryLevel:
      json?.tagBatteryLevel != null ? Number(json.tagBatteryLevel) : null,
    timeInIndirect: Number(json?.timeInIndirect ?? 0),
    totalMovement: Number(json?.totalMovement ?? 0),
  };
}
// ─── YTD Module Completion Modal ──────────────────────────────────────────────

/**
 * Fetch year-to-date module completion data for the production milestones modal.
 *
 * No parameters required - the backend scopes results to the current calendar
 * year automatically and returns one entry per month (Jan through current month).
 * Future months are included with null actuals so the chart x-axis is stable.
 *
 * Consumed by: useYtdModuleCompletionData.js → YtdModuleCompletion modal
 *
 * Response shape (200):
 *   {
 *     metrics: {
 *       ytdCompleted:   number,   - cumulative modules completed this year
 *       ytdTarget:      number,   - cumulative target through current month
 *       ytdPct:         number,   - ytdCompleted / ytdTarget × 100, 1 dp
 *       deltaVsTarget:  number,   - ytdCompleted − ytdTarget (negative = behind)
 *       monthlyAvg:     number,   - avg modules/month across completed months
 *       bestMonth:      string | null,   - "Jan 2026" label of best month
 *       bestMonthCount: number | null,   - modules completed in best month
 *       missedMonths:   number,   - months where completed < target
 *     },
 *     months: Array<{
 *       month:        string,          - "Jan" … "Dec"
 *       target:       number,          - monthly target
 *       completed:    number | null,   - null for future months
 *       cumTarget:    number,          - cumulative target through this month
 *       cumCompleted: number | null,   - null for future months
 *     }>
 *   }
 *
 * @param {AbortSignal} [signal]
 *
 * @returns {Promise<{
 *   metrics: {
 *     ytdCompleted:   number,
 *     ytdTarget:      number,
 *     ytdPct:         number,
 *     deltaVsTarget:  number,
 *     monthlyAvg:     number,
 *     bestMonth:      string | null,
 *     bestMonthCount: number | null,
 *     missedMonths:   number,
 *   },
 *   months: Array<{
 *     month:        string,
 *     target:       number,
 *     completed:    number | null,
 *     cumTarget:    number,
 *     cumCompleted: number | null,
 *   }>
 * }>}
 *
 * @throws {DataReceiverError}
 */
export async function fetchYtdModuleCompletion(signal) {
  const json = await apimGet(
    "ytd-module-completion",
    new URLSearchParams(),
    signal,
  );

  // ── Metrics ───────────────────────────────────────────────────────────────
  // Backend field names differ from the shape the hook expects; map them here.
  // Backend sends: ytdCompletionPct, totalCompleted, totalTarget, monthlyAvg,
  //                monthsMissed
  // Hook expects:  ytdPct, ytdCompleted, ytdTarget, monthlyAvg, missedMonths,
  //                deltaVsTarget (computed), bestMonth/bestMonthCount (derived)
  const rawMetrics = json?.metrics ?? {};

  const totalCompleted = Number(rawMetrics.totalCompleted ?? 0);
  const totalTarget = Number(rawMetrics.totalTarget ?? 0);

  // Derive bestMonth / bestMonthCount from the table (past months only).
  const rawTable = Array.isArray(json?.table) ? json.table : [];
  let bestMonth = null;
  let bestMonthCount = null;
  if (rawTable.length > 0) {
    const best = rawTable.reduce((prev, cur) =>
      (cur.completed ?? -Infinity) > (prev.completed ?? -Infinity) ? cur : prev,
    );
    if (best.completed != null) {
      bestMonth = String(best.month); // e.g. "Mar 2026"
      bestMonthCount = Number(best.completed);
    }
  }

  const metrics = {
    ytdCompleted: totalCompleted,
    ytdTarget: totalTarget,
    ytdPct: Number(rawMetrics.ytdCompletionPct ?? 0),
    deltaVsTarget: totalCompleted - totalTarget, // not sent by backend; compute
    monthlyAvg: Number(rawMetrics.monthlyAvg ?? 0),
    bestMonth,
    bestMonthCount,
    missedMonths: Number(rawMetrics.monthsMissed ?? 0),
  };

  // ── Months ────────────────────────────────────────────────────────────────
  // Backend sends two separate arrays:
  //   graph[]  – 12 entries Jan–Dec, field "actual" (null for future months)
  //   table[]  – past months only, with cumulativeCompleted/cumulativeTarget
  //
  // The hook expects a single merged "months" array with:
  //   { month, target, completed, cumTarget, cumCompleted }
  //
  // Table month labels use "Jan 2026"; graph uses "Jan". Key the lookup on the
  // first 3 chars so they match across both arrays.
  const tableByShortMonth = {};
  rawTable.forEach((row) => {
    const shortKey = String(row.month ?? "").slice(0, 3); // "Jan 2026" → "Jan"
    tableByShortMonth[shortKey] = row;
  });

  const rawGraph = Array.isArray(json?.graph) ? json.graph : [];

  // Build cumTarget as a running sum in case monthly targets ever vary.
  let runningCumTarget = 0;
  const months = rawGraph.map((g) => {
    runningCumTarget += Number(g.target ?? 0);
    const shortKey = String(g.month ?? "").slice(0, 3);
    const tableRow = tableByShortMonth[shortKey];
    return {
      month: String(g.month ?? ""),
      target: Number(g.target ?? 0),
      completed: g.actual != null ? Number(g.actual) : null,
      cumTarget: tableRow
        ? Number(tableRow.cumulativeTarget ?? runningCumTarget)
        : runningCumTarget,
      cumCompleted:
        tableRow?.cumulativeCompleted != null
          ? Number(tableRow.cumulativeCompleted)
          : null,
    };
  });

  return { metrics, months };
}
// ─── Module Completion Variance Modal ────────────────────────────────────────

/**
 * Fetch 8-week module completion variance data for the production milestones modal.
 *
 * No parameters required - the backend derives the 8 most-recent ISO weeks
 * with data automatically. Weeks are padded with synthetic empty entries if
 * fewer than 8 weeks of history exist, so the graph always renders 8 bars.
 *
 * Consumed by: useModuleVarianceData.js → CompletionVariance modal
 *
 * Response shape (200):
 *   {
 *     metrics: {
 *       eightWeekVariancePct: number,   - avg variance % across 8 weeks, 1 dp
 *                                         positive = behind schedule
 *                                         negative = ahead of schedule
 *       target:               number,   - goal from RTLS_ProductionGoals
 *                                         (completionVariance × 100, e.g. 95)
 *       weeksAhead:           number,   - weeks where avg variance < 0
 *       weeksBehind:          number,   - weeks where avg variance > 0
 *     },
 *     graph: Array<{
 *       week:        string,   - "Wk 1" … "Wk 8" (Wk 8 = most recent)
 *       label:       string,   - "Apr 28" (Monday of that ISO week)
 *       variancePct: number,   - % slip for that week
 *     }>,
 *     table: Array<{
 *       woNum:        string,   - work order number
 *       scheduled:    string,   - "YYYY-MM-DD"
 *       actual:       string,   - "YYYY-MM-DD"
 *       varianceDays: number,   - positive = late, negative = early
 *     }>
 *   }
 *
 * @param {AbortSignal} [signal]
 *
 * @returns {Promise<{
 *   metrics: {
 *     eightWeekVariancePct: number,
 *     target:               number,
 *     weeksAhead:           number,
 *     weeksBehind:          number,
 *   },
 *   graph: Array<{
 *     week:        string,
 *     label:       string,
 *     variancePct: number,
 *   }>,
 *   table: Array<{
 *     woNum:        string,
 *     scheduled:    string,
 *     actual:       string,
 *     varianceDays: number,
 *   }>
 * }>}
 *
 * @throws {DataReceiverError}
 */
export async function fetchModuleVariance(signal) {
  const json = await apimGet("module-variance", new URLSearchParams(), signal);

  // Normalise metrics sub-object defensively.
  const raw = json?.metrics ?? {};

  const metrics = {
    eightWeekVariancePct: Number(raw.eightWeekVariancePct ?? 0),
    target: Number(raw.target ?? 95),
    weeksAhead: Number(raw.weeksAhead ?? 0),
    weeksBehind: Number(raw.weeksBehind ?? 0),
  };

  // Normalise graph array - always 8 entries, oldest → newest.
  const graph = Array.isArray(json?.graph)
    ? json.graph.map((g) => ({
        week: String(g.week ?? ""),
        label: String(g.label ?? ""),
        variancePct: Number(g.variancePct ?? 0),
      }))
    : [];

  // Normalise table array - one row per module in the 8-week window,
  // sorted newest-first by the backend.
  const table = Array.isArray(json?.table)
    ? json.table.map((r) => ({
        woNum: String(r.woNum ?? ""),
        scheduled: String(r.scheduled ?? ""),
        actual: String(r.actual ?? ""),
        varianceDays: Number(r.varianceDays ?? 0),
      }))
    : [];

  return { metrics, graph, table };
}

// ─── Weekly SQFT Completed Modal ─────────────────────────────────────────────

/**
 * Fetch 8-week SQFT completion trend data for the production milestones modal.
 *
 * No parameters required - the backend derives the 8 most-recent ISO weeks
 * automatically, always including the current week even if it has no data yet.
 * Weeks are padded with synthetic empty entries if fewer than 8 weeks of
 * history exist so the graph always renders 8 bars.
 *
 * weeklyTarget is sourced live from RTLS_ProductionGoals (PrimaryName =
 * 'sqftCompletion'). GoalValue is stored in thousands (12 → 12 000 sqft/week).
 * eightWeekAvg is retained as a separate informational field - it reflects
 * actual production history and may differ from the goal target.
 *
 * Consumed by: useWeeklySqftData.js → WeeklySqftCompleted modal
 *
 * Response shape (200):
 *   {
 *     metrics: {
 *       sqftThisWeek:  number,   - SQFT completed in the current ISO week
 *       weeklyTarget:  number,   - goal from RTLS_ProductionGoals (e.g. 12000)
 *       eightWeekAvg:  number,   - avg SQFT across non-zero weeks in window
 *     },
 *     graph: Array<{
 *       week:  string,   - "Wk 1" … "Wk 8" (Wk 8 = most recent / current)
 *       label: string,   - "Apr 28" (Monday of that ISO week)
 *       sqft:  number,   - total SQFT completed that week (0 for empty weeks)
 *     }>
 *   }
 *
 * @param {AbortSignal} [signal]
 *
 * @returns {Promise<{
 *   metrics: {
 *     sqftThisWeek: number,
 *     weeklyTarget: number,
 *     eightWeekAvg: number,
 *   },
 *   graph: Array<{
 *     week:  string,
 *     label: string,
 *     sqft:  number,
 *   }>
 * }>}
 *
 * @throws {DataReceiverError}
 */
export async function fetchWeeklySqft(signal) {
  const json = await apimGet("weekly-sqft", new URLSearchParams(), signal);

  // Normalise metrics sub-object defensively.
  const raw = json?.metrics ?? {};

  const metrics = {
    sqftThisWeek: Number(raw.sqftThisWeek ?? 0),
    weeklyTarget: Number(raw.weeklyTarget ?? 12000),
    eightWeekAvg: Number(raw.eightWeekAvg ?? 0),
  };

  // Normalise graph array - always 8 entries, oldest → newest.
  // Wk 8 is the current ISO week and may have 0 sqft if the week just started.
  const graph = Array.isArray(json?.graph)
    ? json.graph.map((g) => ({
        week: String(g.week ?? ""),
        label: String(g.label ?? ""),
        sqft: Number(g.sqft ?? 0),
      }))
    : [];

  return { metrics, graph };
}
// ─── First Pass Rate Modal ────────────────────────────────────────────────────

/**
 * Fetch current-month First Pass Rate (FPR) data for the production milestones modal.
 *
 * No parameters required - the backend scopes results to the current calendar
 * month automatically. The daily graph always contains one entry per day of
 * the current month; days after today (or with no evaluated items) carry
 * fpr: null so the line chart terminates cleanly at today.
 *
 * FPR definition: an item passes first pass if it has no qualifying
 * nonconformant steps at utility stations (8 = Fire Sprinkler, 20 = Plumbing,
 * 22 = Electrical), excluding steps whose only reason matches
 * "Moving mod to next station". FPR % = passes / total × 100.
 *
 * targetFpr is sourced live from RTLS_ProductionGoals (PrimaryName =
 * 'firstPassRate'). GoalValue is stored as a decimal fraction (0.95 → 95).
 *
 * Consumed by: useFirstPassRateData.js → FirstPassRate modal
 *
 * Response shape (200):
 *   {
 *     metrics: {
 *       currentMonthFpr:  number,   - FPR % for current month, 1 dp, default 100
 *       targetFpr:        number,   - goal from RTLS_ProductionGoals (e.g. 95)
 *       weeksMet:         number,   - weeks this month where weekly FPR >= target
 *       weeksUnder:       number,   - weeks this month where weekly FPR < target
 *     },
 *     graph: Array<{
 *       day:    number,          - 1 … (days in current month)
 *       label:  string,          - "Jul 1"
 *       fpr:    number | null,   - null for future days or days with no data
 *       target: number,          - targetFpr echoed on every entry for chart reference line
 *     }>,
 *     tableFailedJobs: Array<{
 *       woNum:          string,     - work order number
 *       itemNum:        string,     - item number
 *       batchNum:       string,     - batch number
 *       failedStations: string[],   - e.g. ["Fire Sprinkler", "Electrical"]
 *       day:            string,     - "YYYY-MM-DD" evaluation date
 *     }>,
 *     tableUtilities: Array<{
 *       utility:    string,   - "Fire Sprinkler" | "Plumbing" | "Electrical"
 *       stationId:  number,   - 8 | 20 | 22
 *       failCount:  number,   - items with a qualifying fail at this station, current month
 *       totalItems: number,   - items that passed through this station, current month
 *       failPct:    number,   - failCount / totalItems × 100, 1 dp
 *     }>
 *   }
 *
 * @param {AbortSignal} [signal]
 *
 * @returns {Promise<{
 *   metrics: {
 *     currentMonthFpr: number,
 *     targetFpr:       number,
 *     weeksMet:        number,
 *     weeksUnder:      number,
 *   },
 *   graph: Array<{
 *     day:    number,
 *     label:  string,
 *     fpr:    number | null,
 *     target: number,
 *   }>,
 *   tableFailedJobs: Array<{
 *     woNum:          string,
 *     itemNum:        string,
 *     batchNum:       string,
 *     failedStations: string[],
 *     day:            string,
 *   }>,
 *   tableUtilities: Array<{
 *     utility:    string,
 *     stationId:  number,
 *     failCount:  number,
 *     totalItems: number,
 *     failPct:    number,
 *   }>
 * }>}
 *
 * @throws {DataReceiverError}
 */
export async function fetchFirstPassRate(signal) {
  const json = await apimGet("first-pass-rate", new URLSearchParams(), signal);

  // Normalise metrics sub-object defensively.
  const raw = json?.metrics ?? {};

  const metrics = {
    currentMonthFpr: Number(raw.currentMonthFpr ?? 100),
    targetFpr: Number(raw.targetFpr ?? 95),
    weeksMet: Number(raw.weeksMet ?? 0),
    weeksUnder: Number(raw.weeksUnder ?? 0),
  };

  // Normalise graph array - one entry per day of the current month.
  // fpr is null for future days / no-data days; guard here in case backend omits the field.
  const graph = Array.isArray(json?.graph)
    ? json.graph.map((g) => ({
        day: Number(g.day ?? 0),
        label: String(g.label ?? ""),
        fpr: g.fpr != null ? Number(g.fpr) : null,
        target: Number(g.target ?? metrics.targetFpr),
      }))
    : [];

  // Normalise failed-jobs table - one row per item that failed FPR this month.
  const tableFailedJobs = Array.isArray(json?.tableFailedJobs)
    ? json.tableFailedJobs.map((j) => ({
        woNum: String(j.woNum ?? ""),
        itemNum: String(j.itemNum ?? ""),
        batchNum: String(j.batchNum ?? ""),
        failedStations: Array.isArray(j.failedStations)
          ? j.failedStations.map((s) => String(s))
          : [],
        day: String(j.day ?? ""),
      }))
    : [];

  // Normalise utilities table - one entry per station (Fire Sprinkler,
  // Plumbing, Electrical). Order matches FPR_STATIONS in the backend [8, 20, 22].
  const tableUtilities = Array.isArray(json?.tableUtilities)
    ? json.tableUtilities.map((u) => ({
        utility: String(u.utility ?? ""),
        stationId: Number(u.stationId ?? 0),
        failCount: Number(u.failCount ?? 0),
        totalItems: Number(u.totalItems ?? 0),
        failPct: Number(u.failPct ?? 0),
      }))
    : [];

  return { metrics, graph, tableFailedJobs, tableUtilities };
}

// ─── Daily Modules Completed Modal ───────────────────────────────────────────

/**
 * Fetch daily module completion data for the current calendar month.
 *
 * No parameters required - the backend scopes results to the current UTC
 * calendar month automatically. Every day of the month is represented in both
 * graph and table; future days carry actual: null and status: "Future" so the
 * chart and table render consistently for the full month at any point.
 *
 * dailyTarget is derived server-side from scheduled mods this month:
 *   CEIL(scheduledThisMonth / workingDaysThisMonth)
 * When no scheduled data exists the fallback comes from RTLS_ProductionGoals
 * (PrimaryName = 'monthlyMods', e.g. 2.00 modules/day).
 *
 * Status thresholds per day (applied server-side):
 *   "Met"     actual >= dailyTarget
 *   "Near"    actual >= dailyTarget × 0.75
 *   "Missed"  actual < dailyTarget × 0.75
 *   "Future"  day has not yet occurred
 *
 * Consumed by: useDailyModulesCompletedData.js → DailyModulesCompleted modal
 *
 * Response shape (200):
 *   {
 *     metrics: {
 *       monthAvgPct:  number,   - avg of daily (actual/target)% across elapsed days, 1 dp
 *       daysMet:      number,   - days with status "Met"
 *       daysNear:     number,   - days with status "Near"
 *       daysMissed:   number,   - days with status "Missed"
 *       dailyTarget:  number,   - modules/day target (derived or goal fallback)
 *     },
 *     graph: Array<{
 *       day:    number,          - 1-based day of month
 *       actual: number | null,   - null for future days
 *       target: number,          - dailyTarget echoed on every entry
 *     }>,
 *     table: Array<{
 *       day:    number,                            - 1-based day of month
 *       date:   string,                            - "YYYY-MM-DD"
 *       target: number,
 *       actual: number | null,                     - null for future days
 *       pct:    number | null,                     - actual/target × 100, 1 dp; null for future
 *       status: "Met" | "Near" | "Missed" | "Future",
 *     }>
 *   }
 *
 * @param {AbortSignal} [signal]
 *
 * @returns {Promise<{
 *   metrics: {
 *     monthAvgPct:  number,
 *     daysMet:      number,
 *     daysNear:     number,
 *     daysMissed:   number,
 *     dailyTarget:  number,
 *   },
 *   graph: Array<{
 *     day:    number,
 *     actual: number | null,
 *     target: number,
 *   }>,
 *   table: Array<{
 *     day:    number,
 *     date:   string,
 *     target: number,
 *     actual: number | null,
 *     pct:    number | null,
 *     status: "Met" | "Near" | "Missed" | "Future",
 *   }>
 * }>}
 *
 * @throws {DataReceiverError}
 */
export async function fetchDailyModulesCompleted(signal) {
  const json = await apimGet(
    "daily-modules-completed",
    new URLSearchParams(),
    signal,
  );

  // Normalise metrics sub-object defensively.
  const raw = json?.metrics ?? {};

  const metrics = {
    monthAvgPct: Number(raw.monthAvgPct ?? 0),
    daysMet: Number(raw.daysMet ?? 0),
    daysNear: Number(raw.daysNear ?? 0),
    daysMissed: Number(raw.daysMissed ?? 0),
    dailyTarget: Number(raw.dailyTarget ?? 2),
  };

  // Normalise graph array - one entry per calendar day of the current month.
  // Future days have actual: null; guard here in case backend omits the field.
  const graph = Array.isArray(json?.graph)
    ? json.graph.map((g) => ({
        day: Number(g.day ?? 0),
        actual: g.actual != null ? Number(g.actual) : null,
        target: Number(g.target ?? metrics.dailyTarget),
      }))
    : [];

  // Normalise table array - same day coverage as graph, with additional
  // date string, pct, and status fields for the daily log table.
  const VALID_STATUSES = new Set(["Met", "Near", "Missed", "Future"]);

  const table = Array.isArray(json?.table)
    ? json.table.map((r) => ({
        day: Number(r.day ?? 0),
        date: String(r.date ?? ""),
        target: Number(r.target ?? metrics.dailyTarget),
        actual: r.actual != null ? Number(r.actual) : null,
        pct: r.pct != null ? Number(r.pct) : null,
        status: VALID_STATUSES.has(r.status) ? r.status : "Future",
      }))
    : [];

  return { metrics, graph, table };
}

// ─── Homepage Metrics ─────────────────────────────────────────────────────────

/**
 * Fetch all 6 homepage numerical metrics in a single round-trip.
 *
 * No parameters required - the backend derives all date boundaries
 * (UTC today, 7-day window, current business week) automatically.
 *
 * Consumed by: useHomepageData.js → HomePage metric cards
 *
 * ── Metric descriptions ───────────────────────────────────────────────────────
 *
 *   buildings          Distinct Property names currently on-line.
 *                      No 7-day avg (point-in-time list, not a count).
 *
 *   modsOnLine         COUNT(*) of currentOnLineMods rows.
 *                      sevenDayAvg: AVG(OnlineModCount) from RTLS_modRollTracker
 *                      over the prior 7 UTC calendar days (today excluded).
 *
 *   activePersonnel    Employees with locationStatus "Active" AND
 *                      currentJob ≠ "Unassigned" right now.
 *                      sevenDayAvg: null — no historical personnel-count table
 *                      exists; the UI should render "—" for this metric.
 *
 *   failedJobsToday    Distinct manufactured items with ≥ 1 Non-Conformance step
 *                      today whose reason is NOT "Moving Mod to Next Station".
 *                      Mirrors the jobsNC predicate in fetchNoncompliances() so
 *                      the homepage card always matches the modal seed count.
 *                      sevenDayAvg: AVG of per-day failed counts over the prior
 *                      7 UTC calendar days (days with zero failures are excluded
 *                      from the average, not zero-padded).
 *
 *   avgDailyRollTime   AVG inter-roll gap in minutes for UTC calendar-today.
 *                      null when fewer than 2 rolls have occurred (no gap exists).
 *                      sevenDayAvg: AVG of per-day avg gaps over the prior 7
 *                      business days (Mon–Fri only; days with ≤ 1 roll excluded).
 *
 *   weeklyProgressPct  (unitsCompleted / weeklyUnitsTarget) × 100 for the
 *                      current Mon–Sun UTC business week, rounded to 2 d.p.
 *                      No 7-day avg — progress resets each week.
 *
 * ── Response shape (200) ─────────────────────────────────────────────────────
 *
 *   {
 *     buildings: {
 *       current: string[]           - distinct Property names on-line
 *     },
 *     modsOnLine: {
 *       current:      number,
 *       sevenDayAvg:  number | null
 *     },
 *     activePersonnel: {
 *       current:      number,
 *       sevenDayAvg:  null           - always null; no historical table
 *     },
 *     failedJobsToday: {
 *       current:      number,
 *       sevenDayAvg:  number | null
 *     },
 *     avgDailyRollTime: {
 *       current:      number | null, - null when < 2 rolls today
 *       sevenDayAvg:  number | null
 *     },
 *     weeklyProgressPct: {
 *       current:      number         - 0–100+ (can exceed 100 if goal surpassed)
 *     },
 *     meta: {
 *       asOf: string                 - ISO-8601 UTC timestamp of query execution
 *     }
 *   }
 *
 * @param {AbortSignal} [signal]
 *
 * @returns {Promise<{
 *   buildings:         { current: string[] },
 *   modsOnLine:        { current: number,      sevenDayAvg: number | null },
 *   activePersonnel:   { current: number,      sevenDayAvg: null },
 *   failedJobsToday:   { current: number,      sevenDayAvg: number | null },
 *   avgDailyRollTime:  { current: number|null, sevenDayAvg: number | null },
 *   weeklyProgressPct: { current: number },
 *   meta: { asOf: string }
 * }>}
 *
 * @throws {DataReceiverError}
 */
export async function fetchHomepageMetrics(signal) {
  const json = await apimGet("homepage-metrics", new URLSearchParams(), signal);

  // ── buildings ─────────────────────────────────────────────────────────────
  // Backend returns { current: string[] }.
  // Guard: ensure current is always a plain string array even if the backend
  // degrades to [] on a SQL failure.
  const buildings = {
    current: Array.isArray(json?.buildings?.current)
      ? json.buildings.current.map((b) => String(b))
      : [],
  };

  // ── normMetricWithAvg ─────────────────────────────────────────────────────
  // Shared normaliser for metrics that carry { current, sevenDayAvg }.
  // sevenDayAvg is preserved as null when the backend returns null (meaning
  // "no data in window") — callers should render "—" rather than "0" for null.
  function normMetricWithAvg(raw) {
    return {
      current: Number(raw?.current ?? 0),
      sevenDayAvg: raw?.sevenDayAvg != null ? Number(raw.sevenDayAvg) : null,
    };
  }

  // ── modsOnLine ────────────────────────────────────────────────────────────
  const modsOnLine = normMetricWithAvg(json?.modsOnLine);

  // ── activePersonnel ───────────────────────────────────────────────────────
  // sevenDayAvg is structurally null from the backend (no historical table).
  // Normalise current defensively; preserve null on avg so the hook/UI can
  // render "—" without special-casing the number zero.
  const activePersonnel = {
    current: Number(json?.activePersonnel?.current ?? 0),
    sevenDayAvg: null,
  };

  // ── failedJobsToday ───────────────────────────────────────────────────────
  const failedJobsToday = normMetricWithAvg(json?.failedJobsToday);

  // ── avgDailyRollTime ──────────────────────────────────────────────────────
  // Both current and sevenDayAvg can legitimately be null (< 2 rolls today,
  // or no roll data in the 7-day window). Preserve null rather than coercing
  // to 0 so the UI can display "—" instead of a misleading zero.
  const avgDailyRollTime = {
    current:
      json?.avgDailyRollTime?.current != null
        ? Number(json.avgDailyRollTime.current)
        : null,
    sevenDayAvg:
      json?.avgDailyRollTime?.sevenDayAvg != null
        ? Number(json.avgDailyRollTime.sevenDayAvg)
        : null,
  };

  // ── weeklyProgressPct ─────────────────────────────────────────────────────
  // Single scalar: (unitsCompleted / unitsTarget) × 100, 2 d.p.
  // Defaults to 0 when no goal is configured or the week has no completions.
  const weeklyProgressPct = {
    current: Number(json?.weeklyProgressPct?.current ?? 0),
  };

  // ── meta ──────────────────────────────────────────────────────────────────
  const meta = {
    asOf: json?.meta?.asOf ?? null,
  };

  return {
    buildings,
    modsOnLine,
    activePersonnel,
    failedJobsToday,
    avgDailyRollTime,
    weeklyProgressPct,
    meta,
  };
}

// ─── Daily Mod Roll Time Modal ────────────────────────────────────────────────

/**
 * Fetch mod roll timing data for the Daily Mod Roll Time modal.
 *
 * No parameters required - the backend scopes today's rolls to UTC
 * calendar-today and the history window to the last 7 business days
 * (Mon–Fri) automatically.
 *
 * Consumed by: useModRollsData.js → ModRollTime modal component
 *
 * ── Data sources ──────────────────────────────────────────────────────────────
 *
 *   todaysRolls      One row per roll event recorded in RTLS_modRollTracker
 *                    for UTC calendar-today. rollTimeMinutes is the gap in
 *                    minutes since the previous roll (null for the first roll
 *                    of the day — no prior event to diff against).
 *
 *   last7BusinessDays  One entry per Mon–Fri business day in the prior 7-day
 *                    window. avgRollTime is null when only one roll occurred
 *                    that day (no inter-roll gap exists). Days with no roll
 *                    events at all are back-filled by the backend with
 *                    avgRollTime: null, avgUnitsPerRoll: 0, lineLeaders: []
 *                    so the array always contains exactly 7 entries.
 *
 * ── Response shape (200) ─────────────────────────────────────────────────────
 *
 *   {
 *     todaysRolls: Array<{
 *       datetime:        string,        - ISO-8601 UTC (RollDate)
 *       rollTimeMinutes: number | null, - inter-roll gap; null for first roll
 *       totalUnitCount:  number,        - OnlineModCount at roll time
 *       lineLeader:      string | null
 *     }>,
 *     last7BusinessDays: Array<{
 *       date:            string,        - "YYYY-MM-DD" UTC
 *       avgRollTime:     number | null, - avg gap minutes; null if ≤ 1 roll
 *       avgUnitsPerRoll: number,        - AVG(OnlineModCount) for that day
 *       lineLeaders:     string[]       - distinct non-null leaders that day
 *     }>
 *   }
 *
 * @param {AbortSignal} [signal]
 *
 * @returns {Promise<{
 *   todaysRolls: Array<{
 *     datetime:        string,
 *     rollTimeMinutes: number | null,
 *     totalUnitCount:  number,
 *     lineLeader:      string | null,
 *   }>,
 *   last7BusinessDays: Array<{
 *     date:            string,
 *     avgRollTime:     number | null,
 *     avgUnitsPerRoll: number,
 *     lineLeaders:     string[],
 *   }>
 * }>}
 *
 * @throws {DataReceiverError}
 */
export async function fetchModalRollTime(signal) {
  const json = await apimGet("modal-roll-time", new URLSearchParams(), signal);

  // ── todaysRolls ───────────────────────────────────────────────────────────
  // One row per roll event today, ordered chronologically by the backend.
  // rollTimeMinutes is null for the first roll of the day — preserve null so
  // the UI can render "—" rather than a misleading zero in the table cell.
  const todaysRolls = Array.isArray(json?.todaysRolls)
    ? json.todaysRolls.map((row) => ({
        datetime: String(row?.datetime ?? ""),
        rollTimeMinutes:
          row?.rollTimeMinutes != null ? Number(row.rollTimeMinutes) : null,
        totalUnitCount: Number(row?.totalUnitCount ?? 0),
        lineLeader: row?.lineLeader != null ? String(row.lineLeader) : null,
      }))
    : [];

  // ── last7BusinessDays ─────────────────────────────────────────────────────
  // Always exactly 7 entries (back-filled by the backend for days with no
  // roll events). avgRollTime is null when ≤ 1 roll occurred that day —
  // preserve null for the same reason as rollTimeMinutes above.
  // lineLeaders is always an array; guard against a non-array backend response
  // (e.g. a degraded SQL result returning null for the STRING_AGG column).
  const last7BusinessDays = Array.isArray(json?.last7BusinessDays)
    ? json.last7BusinessDays.map((day) => ({
        date: String(day?.date ?? ""),
        avgRollTime: day?.avgRollTime != null ? Number(day.avgRollTime) : null,
        avgUnitsPerRoll: Number(day?.avgUnitsPerRoll ?? 0),
        lineLeaders: Array.isArray(day?.lineLeaders)
          ? day.lineLeaders.map((l) => String(l))
          : [],
      }))
    : [];

  return { todaysRolls, last7BusinessDays };
}

// ─── Personnel Activity History ───────────────────────────────────────────────

/**
 * Fetch today's full job activity history for a single employee.
 *
 * Consumed by: usePersonnelHistory.js → PersonnelActivityHistory modal
 *
 * The backend queries RTLS_PersonnelMetricsActivityHist filtered on EmpID and
 * returns one row per job assignment, ordered StartTime ASC. Duration is
 * pre-formatted server-side ("Xh Ym" | "Xh" | "Ym") so the table can render
 * it directly.
 *
 * @param {{ employeeId: string }} params
 * @param {AbortSignal} [signal]
 *
 * @returns {Promise<{
 *   activityHistory: Array<{
 *     jobId:     string | null,   - JobID
 *     jobName:   string | null,   - Description
 *     startTime: string | null,   - raw nvarchar, e.g. "09:22 AM"
 *     endTime:   string | null,   - raw nvarchar, e.g. "12:18 PM"
 *     duration:  string | null,   - pre-formatted, e.g. "2h 37m"
 *     hours:     number | null,   - decimal hours
 *     eventDate: string | null,   - ISO-8601 UTC
 *   }>,
 *   count: number
 * }>}
 *
 * @throws {DataReceiverError}
 */
export async function fetchActivityHistory({ employeeId }, signal) {
  if (!employeeId?.trim()) {
    throw new DataReceiverError(
      "fetchActivityHistory: 'employeeId' is required.",
      400,
      null,
    );
  }

  const params = new URLSearchParams({ employeeId: employeeId.trim() });
  const json = await apimGet("activity-personnel", params, signal);

  // Normalise the activityHistory array defensively - guards against partial
  // backend responses and ensures every field is the expected type.
  const rawRows = Array.isArray(json?.activityHistory)
    ? json.activityHistory
    : [];

  const activityHistory = rawRows.map((row) => ({
    jobId: row.jobId ?? null,
    jobName: row.jobName ?? null,
    startTime: row.startTime ?? null,
    endTime: row.endTime ?? null,
    duration: row.duration ?? null,
    hours: row.hours != null ? Number(row.hours) : null,
    eventDate: row.eventDate ?? null,
  }));

  return { activityHistory, count: activityHistory.length };
}
// ─── Personnel Shift Summary ──────────────────────────────────────────────────

/**
 * Fetch today's shift summary for a single employee.
 *
 * Consumed by: useShiftSummary.js → ShiftSummary modal
 *
 * The backend queries two tables scoped to UTC calendar-today:
 *   • RTLS_PersonnelMetricsShiftSum   one row per job assignment
 *   • RTLS_stationsVisited            one row per geofence entry
 *
 * All time arithmetic (active vs idle minutes, clock-in/out extraction) is
 * performed server-side; this function only validates and normalises the
 * already-computed fields so the modal can render them directly.
 *
 * ── Data contract (mirrors ShiftSummary modal prop: person.shiftSummary) ──────
 *
 *   shiftStart            string | null   "hh:mm AM/PM"; null if no Clockin row
 *   shiftEnd              string | null   "hh:mm AM/PM"; null if employee is
 *                                         still on shift (no Clockout row yet)
 *   totalActiveTime       string          pre-formatted "Xh Ym" / "Xh" / "Ym" / "0m"
 *   totalIdleTime         string          same format as totalActiveTime
 *   jobsCompleted         number          completed real-work assignments today
 *   jobsInProgress        number          open assignments (EndTime = 12:00 AM sentinel)
 *   nonconformancesRaised number          always 0 until per-shift NC source is wired in
 *   stationsVisited       string[]        distinct FenceName values, alphabetical
 *
 * ── Normalisation notes ───────────────────────────────────────────────────────
 *
 *   • shiftStart / shiftEnd are passed through as-is (raw nvarchar strings from
 *     the DB).  Null is preserved so the UI can render "—" for an open shift.
 *   • totalActiveTime / totalIdleTime are pre-formatted by the backend.
 *     They will never be an empty string; the backend guarantees "0m" as the
 *     minimum value.  String() coercion here is purely a defensive guard.
 *   • jobsCompleted / jobsInProgress / nonconformancesRaised are coerced with
 *     Number() and floored to 0 to guard against null/undefined backend values.
 *   • stationsVisited is guaranteed to be an array by the backend (empty when
 *     no RTLS fence crossings are recorded for today).  Each entry is coerced
 *     to string and blank entries are filtered out defensively.
 *
 * @param {{ employeeNumber: string }} params
 *   employeeNumber  The employee's EmpID string (e.g. "101026" or "C100218").
 *                   Must match the EmpID column in RTLS_PersonnelMetricsShiftSum
 *                   and the EmployeeId column in RTLS_stationsVisited.
 *
 * @param {AbortSignal} [signal]
 *   Optional AbortController signal for cancellation (e.g. modal close / unmount).
 *
 * @returns {Promise<{
 *   shiftStart:            string | null,
 *   shiftEnd:              string | null,
 *   totalActiveTime:       string,
 *   totalIdleTime:         string,
 *   jobsCompleted:         number,
 *   jobsInProgress:        number,
 *   nonconformancesRaised: number,
 *   stationsVisited:       string[],
 * }>}
 *
 * @throws {DataReceiverError}
 *   400 – employeeNumber is missing or blank (client-side guard, never reaches APIM)
 *   Any non-2xx from the backend is re-thrown as DataReceiverError with the
 *   backend's error.message and HTTP status preserved.
 */
export async function fetchShiftSummary({ employeeNumber }, signal) {
  // ── Client-side param guard ───────────────────────────────────────────────
  //
  // Validate before hitting the network so the modal can show a meaningful
  // error immediately rather than waiting for a round-trip 400.
  if (!employeeNumber?.trim()) {
    throw new DataReceiverError(
      "fetchShiftSummary: 'employeeNumber' is required.",
      400,
      null,
    );
  }

  const params = new URLSearchParams({
    employeeNumber: employeeNumber.trim(),
  });

  const json = await apimGet("shift-summary", params, signal);

  // ── Normalise scalar time strings ─────────────────────────────────────────
  //
  // shiftStart / shiftEnd: preserve null (open shift or no clock record).
  // Any truthy string is passed through as-is — the modal renders the raw
  // "hh:mm AM/PM" format directly.
  const shiftStart = json?.shiftStart != null ? String(json.shiftStart) : null;
  const shiftEnd = json?.shiftEnd != null ? String(json.shiftEnd) : null;

  // ── Normalise pre-formatted duration strings ───────────────────────────────
  //
  // The backend guarantees a non-empty string ("0m" minimum). String() coercion
  // here is a defensive guard against an unexpected null in a degraded response.
  const totalActiveTime = String(json?.totalActiveTime ?? "0m");
  const totalIdleTime = String(json?.totalIdleTime ?? "0m");

  // ── Normalise numeric counters ────────────────────────────────────────────
  //
  // Math.max(0, …) prevents negative values from a malformed backend response
  // causing the UI to render a red "-1" badge.
  const jobsCompleted = Math.max(0, Number(json?.jobsCompleted ?? 0));
  const jobsInProgress = Math.max(0, Number(json?.jobsInProgress ?? 0));
  const nonconformancesRaised = Math.max(
    0,
    Number(json?.nonconformancesRaised ?? 0),
  );

  // ── Normalise stationsVisited array ──────────────────────────────────────
  //
  // Backend returns an alphabetically sorted string[] of distinct FenceName
  // values. Guard against a non-array response and filter empty strings.
  const stationsVisited = Array.isArray(json?.stationsVisited)
    ? json.stationsVisited.map((s) => String(s ?? "").trim()).filter(Boolean)
    : [];

  return {
    shiftStart,
    shiftEnd,
    totalActiveTime,
    totalIdleTime,
    jobsCompleted,
    jobsInProgress,
    nonconformancesRaised,
    stationsVisited,
  };
}

// ─── Production Milestones (page-load) ───────────────────────────────────────
//
// Single entry point for the Production Milestones page load. Returns all five
// top-metric KPIs, the weekly mod-rolls table, and the monthly trend chart in
// one round-trip.
//
// Consumed by: useProductionMilestones.js → ProductionMilestonesPage
//
// Optional params:
//   weekOffset  integer, 0 = current week (default), -1 = previous, etc.
//               Clamps to [-12, 0] server-side. Affects only the rollsTable
//               section; metric values always reflect the current week/month/YTD.
//
// ─── Backend response shape (200) ────────────────────────────────────────────
//
//   {
//     metrics: {
//       ytdModules:    { value: number, target: number },
//       variancePct:   { value: number, target: number },   // on-time rate 0–100
//       weeklySqft:    { value: number, target: number },
//       firstPassRate: { value: number, target: number },   // FPR % this month
//       dailyModules:  { value: number, target: number },
//     },
//     rollsTable: Array<{
//       date:           string,          // "YYYY-MM-DD"
//       dayLabel:       string,          // "Mon" … "Fri"
//       lineLeader:     string | null,
//       projectedRolls: number,
//       actualRolls:    number,
//       projectedWraps: number,
//       actualWraps:    number,
//     }>,  // always 5 entries (Mon–Fri of the requested week)
//     trendChart: {
//       month:             string,       // "Jun 2026"
//       days: Array<{
//         day:       number,             // 1-based day of month
//         date:      string,             // "YYYY-MM-DD"
//         sqft:      number | null,      // null for future days
//         personnel: number | null,      // null for future days
//       }>,
//       monthAvgSqft:      number,
//       monthAvgPersonnel: number,
//     },
//   }
//
// ─── Normalisation notes ──────────────────────────────────────────────────────
//
//   metrics.*  Each sub-object coerced to { value: number, target: number }
//              with safe fallbacks (0 / sensible domain default) so KPI cards
//              can render immediately without null checks.
//
//   rollsTable  Always 5 entries after normalisation (one per Mon–Fri slot).
//               Missing days (e.g. a week only partially stored) are padded with
//               zeros client-side here so the table never has undefined cells.
//
//   trendChart.days  Days with sqft/personnel null are future days; the chart
//                    should use connectNulls={false} to break the line at gaps.
//                    Weekend days may also carry sqft: 0 rather than null when
//                    no production occurred — both cases are handled by the chart.

/**
 * Fetch page-load data for the Production Milestones dashboard.
 *
 * @param {{ weekOffset?: number }} [params]
 *   weekOffset  Which roll-table week to fetch relative to the current ISO week.
 *               0 (default) = current week, -1 = last week. Clamps to [-12, 0].
 *
 * @param {AbortSignal} [signal]
 *
 * @returns {Promise<{
 *   metrics: {
 *     ytdModules:    { value: number, target: number },
 *     variancePct:   { value: number, target: number },
 *     weeklySqft:    { value: number, target: number },
 *     firstPassRate: { value: number, target: number },
 *     dailyModules:  { value: number, target: number },
 *   },
 *   rollsTable: Array<{
 *     date:           string,
 *     dayLabel:       string,
 *     lineLeader:     string | null,
 *     projectedRolls: number,
 *     actualRolls:    number,
 *     projectedWraps: number,
 *     actualWraps:    number,
 *   }>,
 *   trendChart: {
 *     month:             string,
 *     days: Array<{
 *       day:       number,
 *       date:      string,
 *       sqft:      number | null,
 *       personnel: number | null,
 *     }>,
 *     monthAvgSqft:      number,
 *     monthAvgPersonnel: number,
 *   },
 * }>}
 *
 * @throws {DataReceiverError}
 */
export async function fetchProductionMilestones(
  { weekOffset = 0 } = {},
  signal,
) {
  // Clamp weekOffset to the same range the backend enforces so the URL
  // parameter is always valid and error messages are meaningful.
  const clampedOffset = Math.max(-12, Math.min(0, Math.round(weekOffset)));

  const params = new URLSearchParams();
  if (clampedOffset !== 0) {
    // Only include the param when non-zero to keep the default request clean.
    params.set("weekOffset", String(clampedOffset));
  }

  const json = await apimGet("production-milestones", params, signal);

  // ── Metrics normalisation ────────────────────────────────────────────────
  //
  // Each metric is { value, target }. Separate fallback targets chosen to
  // match the domain: SQFT in thousands (10 500), FPR / variance as
  // percentages (95), module counts as small integers (0 → no floor assumed).

  /**
   * Normalise a { value, target } metric sub-object. Both fields are coerced
   * with Number(); NaN / undefined / null all collapse to the supplied fallback.
   *
   * @param {unknown}  raw           - Raw value from the response
   * @param {number}   valueFallback - Default when value is absent/invalid
   * @param {number}   targetFallback - Default when target is absent/invalid
   */
  function normMetric(raw, valueFallback = 0, targetFallback = 0) {
    const value = raw?.value != null ? Number(raw.value) : valueFallback;
    const target = raw?.target != null ? Number(raw.target) : targetFallback;
    return {
      value: Number.isFinite(value) ? value : valueFallback,
      target: Number.isFinite(target) ? target : targetFallback,
    };
  }

  const rawMetrics = json?.metrics ?? {};

  const metrics = {
    ytdModules: normMetric(rawMetrics.ytdModules, 0, 0),
    variancePct: normMetric(rawMetrics.variancePct, 0, 95),
    weeklySqft: normMetric(rawMetrics.weeklySqft, 0, 10_500),
    firstPassRate: normMetric(rawMetrics.firstPassRate, 0, 95),
    dailyModules: normMetric(rawMetrics.dailyModules, 0, 2),
  };

  // ── Rolls table normalisation ────────────────────────────────────────────
  //
  // Backend guarantees 5 entries (Mon–Fri) but we pad defensively to always
  // return exactly 5 rows so the table component can map over DAY_LABELS
  // without length checks.

  const DAY_LABELS_ORDER = ["Mon", "Tue", "Wed", "Thu", "Fri"];

  const rawRolls = Array.isArray(json?.rollsTable) ? json.rollsTable : [];

  // Normalise whatever entries exist from the backend.
  const normRolls = rawRolls.map((row) => ({
    date: String(row.date ?? ""),
    dayLabel: String(row.dayLabel ?? ""),
    lineLeader: row.lineLeader != null ? String(row.lineLeader) : null,
    projectedRolls: Math.max(0, Number(row.projectedRolls ?? 0)),
    actualRolls: Math.max(0, Number(row.actualRolls ?? 0)),
    projectedWraps: Math.max(0, Number(row.projectedWraps ?? 0)),
    actualWraps: Math.max(0, Number(row.actualWraps ?? 0)),
  }));

  // Pad to exactly 5 entries — one per Mon–Fri — in case the backend returned
  // a short array (e.g. only 3 days stored for a partial week).
  const rollsTable = DAY_LABELS_ORDER.map((dayLabel, i) => {
    const existing = normRolls[i];
    // Prefer the existing entry if it exists; otherwise create a zero-padded slot.
    return (
      existing ?? {
        date: "",
        dayLabel,
        lineLeader: null,
        projectedRolls: 0,
        actualRolls: 0,
        projectedWraps: 0,
        actualWraps: 0,
      }
    );
  });

  // ── Trend chart normalisation ────────────────────────────────────────────
  //
  // days array: sqft and personnel are number | null.
  //   null = future day (no production yet)
  //   0    = past business day with no recorded output (valid data point)
  //
  // The chart should use connectNulls={false} so the line breaks between
  // past and future days rather than drawing a misleading straight line to zero.

  const rawTrend = json?.trendChart ?? {};
  const rawDays = Array.isArray(rawTrend.days) ? rawTrend.days : [];

  const days = rawDays.map((d) => ({
    day: Math.max(1, Number(d.day ?? 1)),
    date: String(d.date ?? ""),
    sqft: d.sqft != null ? Math.max(0, Number(d.sqft)) : null,
    personnel: d.personnel != null ? Math.max(0, Number(d.personnel)) : null,
  }));

  const trendChart = {
    month: String(rawTrend.month ?? ""),
    days,
    monthAvgSqft: Math.max(0, Number(rawTrend.monthAvgSqft ?? 0)),
    monthAvgPersonnel: Math.max(0, Number(rawTrend.monthAvgPersonnel ?? 0)),
  };

  return { metrics, rollsTable, trendChart };
}
// ─── Weekly Mod Rolls (filtered) ──────────────────────────────────────────────
//
// Fetches the Mon–Fri roll/wrap table + chart data for one business week,
// for the Weekly Mod Rolls filter panel on ProductionMilestonesPage.
//
// Consumed by: useWeeklyRolls.js → ProductionMilestonesPage
//
// ── How callers determine year / monthIdx / weekNum ──────────────────────────
//
// ProductionMilestonesPage resolves these three values BEFORE calling this
// fetcher, using one of two paths:
//
//   1. Normal page load (no deep-link)
//        → today's date is resolved via getWeekKeyForDate(todayStr)
//        → produces { year, monthIdx, weekKey: "YYYY-monthIdx-Wn" }
//
//   2. Auto-filter from ModRollTime modal click
//        → navigate("/production-milestones", { state: { rollDate } })
//        → the clicked row's date is resolved via getWeekKeyForDate(rollDate)
//        → produces the same { year, monthIdx, weekKey } shape
//
// Both paths converge on the same weekKey format ("YYYY-monthIdx-Wn"), so this
// fetcher accepts the already-resolved { year, monthIdx, weekNum } trio rather
// than re-deriving them from a raw date — that resolution logic stays in
// getWeekKeyForDate / computeBusinessWeeks on the page, where it already lives
// and is unit-testable against the WEEKS_DATA key format.
//
// ── weekKey parsing ────────────────────────────────────────────────────────
//
// If a caller has a raw weekKey string instead of pre-split values, use the
// included parseWeekKey() helper before calling fetchWeeklyRolls — keeps the
// "YYYY-monthIdx-Wn" parsing logic in one place rather than duplicated at
// every call site.
//
// ─── Backend response shape (200) ────────────────────────────────────────────
//
//   {
//     weekLabel:     string,            // "Jun 9 – Jun 13"
//     weekStart:     string,            // "YYYY-MM-DD" Monday
//     weekEnd:       string,            // "YYYY-MM-DD" Friday
//     spansBoundary: boolean,
//     rollLeader:    string | null,
//     days: {
//       Mon: { projRolls, actRolls, projWraps, actWraps },
//       Tue: { … }, Wed: { … }, Thu: { … }, Fri: { … }
//     }
//   }
//
// ── Normalisation notes ───────────────────────────────────────────────────────
//
//   days.*  Each day's four counts are coerced to non-negative numbers so the
//           table/chart never render NaN or negative bars from a malformed
//           response.
//
//   This response shape is a drop-in match for WEEKS_DATA[year][key] — no
//   reshaping needed by the page beyond renaming `weekLabel` → `label` if the
//   page's render code expects that exact key (see hook in output 2/3).

/**
 * Parse a business-week key in "YYYY-monthIdx-Wn" format (the format produced
 * by computeBusinessWeeks / getWeekKeyForDate on the page) into its three
 * numeric parts.
 *
 * @param {string} weekKey  e.g. "2026-5-W3"
 * @returns {{ year: number, monthIdx: number, weekNum: number } | null}
 *          null if the key doesn't match the expected format
 */
export function parseWeekKey(weekKey) {
  if (typeof weekKey !== "string") return null;

  const match = /^(\d{4})-(\d{1,2})-W(\d+)$/.exec(weekKey.trim());
  if (!match) return null;

  const year = Number(match[1]);
  const monthIdx = Number(match[2]);
  const weekNum = Number(match[3]);

  if (
    !Number.isFinite(year) ||
    !Number.isFinite(monthIdx) ||
    monthIdx < 0 ||
    monthIdx > 11 ||
    !Number.isFinite(weekNum) ||
    weekNum < 1
  ) {
    return null;
  }

  return { year, monthIdx, weekNum };
}

/**
 * Fetch the Mon–Fri rolls/wraps table for a single business week.
 *
 * @param {{ year: number, monthIdx: number, weekNum: number }} params
 *   year      Full calendar year (e.g. 2026)
 *   monthIdx  0-based month index (0 = Jan … 11 = Dec) — the month that owns
 *             the week key, matching computeBusinessWeeks' convention.
 *   weekNum   1-based week number within that month (the "n" in "Wn").
 *
 * @param {AbortSignal} [signal]
 *
 * @returns {Promise<{
 *   weekLabel:     string,
 *   weekStart:     string,
 *   weekEnd:       string,
 *   spansBoundary: boolean,
 *   rollLeader:    string | null,
 *   days: Record<"Mon"|"Tue"|"Wed"|"Thu"|"Fri", {
 *     projRolls: number,
 *     actRolls:  number,
 *     projWraps: number,
 *     actWraps:  number,
 *   }>,
 * }>}
 *
 * @throws {DataReceiverError}  Also throws a descriptive error (not via APIM)
 *                              if year/monthIdx/weekNum are missing or invalid
 *                              — fails fast on the client before a wasted
 *                              round-trip to a route that will 400 anyway.
 */
export async function fetchWeeklyRolls(
  { year, monthIdx, weekNum } = {},
  signal,
) {
  // ── Client-side validation ────────────────────────────────────────────────
  //
  // Mirrors the backend's own validation (year 2020–2099, monthIdx 0–11,
  // weekNum ≥ 1) so a bad caller gets an immediate, clear error instead of a
  // round-trip that comes back as a 400 from APIM.

  if (
    !Number.isFinite(year) ||
    year < 2020 ||
    year > 2099 ||
    !Number.isFinite(monthIdx) ||
    monthIdx < 0 ||
    monthIdx > 11 ||
    !Number.isFinite(weekNum) ||
    weekNum < 1
  ) {
    throw new DataReceiverError(
      "fetchWeeklyRolls requires { year: 2020-2099, monthIdx: 0-11, weekNum: >=1 }. " +
        `Received: year=${year}, monthIdx=${monthIdx}, weekNum=${weekNum}`,
      0,
      null,
    );
  }

  const params = new URLSearchParams({
    year: String(year),
    monthIdx: String(monthIdx),
    weekNum: String(weekNum),
  });

  const json = await apimGet("weekly-rolls", params, signal);

  // ── Normalisation ─────────────────────────────────────────────────────────
  //
  // Coerce each day's four counts to non-negative numbers. The backend always
  // returns all five Mon–Fri keys, but we defensively re-build the days object
  // here so a partial/malformed response still gives the table five clean rows
  // rather than throwing inside a render.

  const DAY_KEYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
  const rawDays = json?.days ?? {};

  const days = {};
  for (const key of DAY_KEYS) {
    const d = rawDays[key] ?? {};
    days[key] = {
      projRolls: Math.max(0, Number(d.projRolls ?? 0)),
      actRolls: Math.max(0, Number(d.actRolls ?? 0)),
      projWraps: Math.max(0, Number(d.projWraps ?? 0)),
      actWraps: Math.max(0, Number(d.actWraps ?? 0)),
    };
  }

  return {
    weekLabel: String(json?.weekLabel ?? ""),
    weekStart: String(json?.weekStart ?? ""),
    weekEnd: String(json?.weekEnd ?? ""),
    spansBoundary: Boolean(json?.spansBoundary),
    rollLeader: json?.rollLeader != null ? String(json.rollLeader) : null,
    days,
  };
}

// ─── Monthly Production Trend (filtered) ──────────────────────────────────────
//
// Fetches the sparse daily SQFT/personnel series for one calendar month, for
// the Monthly Production Trend chart on ProductionMilestonesPage.
//
// Consumed by: useMonthlyTrend.js → ProductionMilestonesPage
//
// ── Filter source ─────────────────────────────────────────────────────────────
//
// Unlike Weekly Rolls, the Monthly Trend filter has no modal deep-link path —
// it's a standalone year/month picker that defaults to the current calendar
// month on page load (appliedTrendYear / appliedTrendMonth, both initialised
// from `new Date()`).
//
// ─── Backend response shape (200) ────────────────────────────────────────────
//
//   {
//     month: string,               // "Jun 2026"
//     days: [
//       { day: number, sqft: number, personnel: number },
//       …  (sparse — only days with recorded activity, ascending)
//     ],
//     stats: {
//       avgSqft:  number,
//       peakSqft: number,
//       peakDay:  number,
//       avgPers:  number,
//     } | null   // null when days is empty
//   }
//
// ── Normalisation notes ───────────────────────────────────────────────────────
//
//   days        Stays sparse on purpose — the page's buildFullMonthData()
//               already expands this into a full calendar month with null
//               gaps for the chart's connectNulls={false} break behaviour.
//               Re-expanding here would duplicate that logic and risk drift
//               from the page's own day-of-week / weekend handling.
//
//   stats       Passed through as-is (null-checked) so trendStats on the page
//               can read it directly instead of re-deriving avg/peak client-side.

/**
 * Fetch the sparse daily SQFT/personnel trend for a single calendar month.
 *
 * @param {{ year: number, monthIdx: number }} params
 *   year      Full calendar year (e.g. 2026)
 *   monthIdx  0-based month index (0 = Jan … 11 = Dec), matching the
 *             MONTHLY_TREND `${year}-${monthIdx}` key convention on the page.
 *
 * @param {AbortSignal} [signal]
 *
 * @returns {Promise<{
 *   month: string,
 *   days: Array<{ day: number, sqft: number, personnel: number }>,
 *   stats: { avgSqft: number, peakSqft: number, peakDay: number, avgPers: number } | null,
 * }>}
 *
 * @throws {DataReceiverError}  Also throws client-side if year/monthIdx are
 *                              missing or out of range, before any network call.
 */
export async function fetchMonthlyTrend({ year, monthIdx } = {}, signal) {
  // ── Client-side validation ────────────────────────────────────────────────

  if (
    !Number.isFinite(year) ||
    year < 2020 ||
    year > 2099 ||
    !Number.isFinite(monthIdx) ||
    monthIdx < 0 ||
    monthIdx > 11
  ) {
    throw new DataReceiverError(
      "fetchMonthlyTrend requires { year: 2020-2099, monthIdx: 0-11 }. " +
        `Received: year=${year}, monthIdx=${monthIdx}`,
      0,
      null,
    );
  }

  const params = new URLSearchParams({
    year: String(year),
    monthIdx: String(monthIdx),
  });

  const json = await apimGet("monthly-trend", params, signal);

  // ── Normalisation ─────────────────────────────────────────────────────────
  //
  // days: keep sparse, coerce each entry's three numeric fields.
  // stats: pass through with the same { avgSqft, peakSqft, peakDay, avgPers }
  //        shape the page's own trendStats useMemo currently produces, or null.

  const rawDays = Array.isArray(json?.days) ? json.days : [];

  const days = rawDays.map((d) => ({
    day: Math.max(1, Number(d.day ?? 1)),
    sqft: Math.max(0, Number(d.sqft ?? 0)),
    personnel: Math.max(0, Number(d.personnel ?? 0)),
  }));

  const rawStats = json?.stats ?? null;

  const stats = rawStats
    ? {
        avgSqft: Math.max(0, Number(rawStats.avgSqft ?? 0)),
        peakSqft: Math.max(0, Number(rawStats.peakSqft ?? 0)),
        peakDay: Math.max(1, Number(rawStats.peakDay ?? 1)),
        avgPers: Math.max(0, Number(rawStats.avgPers ?? 0)),
      }
    : null;

  return {
    month: String(json?.month ?? ""),
    days,
    stats,
  };
}

/**
 * Fetch the combined goal-configuration snapshot for the "Edit Goals" modal.
 *
 * Backed by dataServe's goals-management route, which pulls all 3 goal
 * tables in a single round-trip:
 *   - RTLS_weeklyRollsGoals   current business week (Mon–Fri) roll/wrap targets
 *   - RTLS_ProductionGoals    static homepage + production-milestones goals
 *   - RTLS_ytdCompletionGoal  current-year monthly YTD targets (Jan–Dec)
 *
 * No parameters required — the backend always resolves "current business
 * week" and "current UTC year" itself so the modal can't drift from what the
 * rest of the dashboard considers "this week" / "this year".
 *
 * Consumed by: useProductionGoals.js → Edit Goals modal
 *
 * ── Normalisation notes ──────────────────────────────────────────────────────
 *
 * Every array/object is defended against a missing or malformed backend
 * response (returns [] / 0 / null rather than letting the modal throw on a
 * partial 200, which the backend can legitimately return if one of its 3
 * underlying queries failed and degraded gracefully).
 *
 * goalId fields are passed through as-is (string | null). The modal should
 * treat goalId === null as "no row exists yet for this goal" — i.e. a future
 * save action would INSERT rather than UPDATE.
 *
 * Response shape (200) — see handleGoalsManagement in dataServe.js for the
 * authoritative source of truth:
 *   {
 *     weeklyRollGoals: {
 *       weekStart:   string,   "YYYY-MM-DD"
 *       weekEnd:     string,   "YYYY-MM-DD"
 *       lineLeader:  string | null
 *       days: Array<{
 *         date:       string,        "YYYY-MM-DD"
 *         dayLabel:   string,        "Mon" … "Fri"
 *         roll:       number,
 *         rollGoalId: string | null,
 *         wrap:       number,
 *         wrapGoalId: string | null,
 *       }>
 *     },
 *     productionGoals: Array<{
 *       goalId:    string | null,
 *       goalName:  string,
 *       goalValue: number,
 *       updatedAt: string | null,
 *     }>,
 *     ytdCompletionGoals: {
 *       year: number,
 *       months: Array<{
 *         month:     string,
 *         monthDate: string,
 *         goalValue: number,
 *         goalId:    string | null,
 *         hasGoal:   boolean,
 *       }>
 *     },
 *     meta: { asOf: string }
 *   }
 *
 * @param {AbortSignal} [signal]
 * @param {{ weekDate?: string }} [options]
 *   weekDate - optional "YYYY-MM-DD". Resolves the Mon–Fri business week
 *   containing this date for weeklyRollGoals. Omit for "today's" week.
 *   Does not affect productionGoals or ytdCompletionGoals.
 *
 * @returns {Promise<{
 *   weeklyRollGoals: {
 *     weekStart: string,
 *     weekEnd:   string,
 *     lineLeader:   string | null,
 *     days: Array<{
 *       date:       string,
 *       dayLabel:   string,
 *       roll:       number,
 *       rollGoalId: string | null,
 *       wrap:       number,
 *       wrapGoalId: string | null,
 *     }>,
 *   },
 *   productionGoals: Array<{
 *     goalId:    string | null,
 *     goalName:  string,
 *     goalValue: number,
 *     updatedAt: string | null,
 *   }>,
 *   ytdCompletionGoals: {
 *     year: number,
 *     months: Array<{
 *       month:     string,
 *       monthDate: string,
 *       goalValue: number,
 *       goalId:    string | null,
 *       hasGoal:   boolean,
 *     }>,
 *   },
 *   meta: { asOf: string | null },
 * }>}
 *
 * @throws {DataReceiverError}
 */
export async function fetchGoalsManagement(signal, { weekDate } = {}) {
  const params = new URLSearchParams();
  if (weekDate) params.set("date", weekDate);

  const json = await apimGet("goals-management", params, signal);

  // ── weeklyRollGoals ─────────────────────────────────────────────────────────
  const rawWeekly = json?.weeklyRollGoals ?? {};

  // Normalise lineLeader: trim, coerce empty/whitespace-only to null so the
  // hook and UI always receive either a non-empty string or null — never "".
  const rawLineLeader = rawWeekly.lineLeader;
  const lineLeader =
    rawLineLeader != null && String(rawLineLeader).trim()
      ? String(rawLineLeader).trim()
      : null;

  const weeklyRollGoals = {
    weekStart: rawWeekly.weekStart ?? null,
    weekEnd: rawWeekly.weekEnd ?? null,
    lineLeader,
    days: Array.isArray(rawWeekly.days)
      ? rawWeekly.days.map((d) => ({
          date: d.date ?? null,
          dayLabel: d.dayLabel ?? "",
          roll: Number(d.roll ?? 0),
          rollGoalId: d.rollGoalId ?? null,
          wrap: Number(d.wrap ?? 0),
          wrapGoalId: d.wrapGoalId ?? null,
        }))
      : [],
  };

  // ── productionGoals ─────────────────────────────────────────────────────────
  const productionGoals = Array.isArray(json?.productionGoals)
    ? json.productionGoals.map((g) => ({
        goalId: g.goalId ?? null,
        goalName: g.goalName ?? "",
        goalValue: Number(g.goalValue ?? 0),
        unitType:
          g.unitType != null
            ? String(g.unitType).trim().toLowerCase()
            : "numeric",
        updatedAt: g.updatedAt ?? null,
      }))
    : [];

  // ── ytdCompletionGoals ──────────────────────────────────────────────────────
  const rawYtd = json?.ytdCompletionGoals ?? {};

  const ytdCompletionGoals = {
    year: Number(rawYtd.year ?? new Date().getUTCFullYear()),
    months: Array.isArray(rawYtd.months)
      ? rawYtd.months.map((m) => ({
          month: m.month ?? "",
          monthDate: m.monthDate ?? null,
          goalValue: Number(m.goalValue ?? 0),
          goalId: m.goalId ?? null,
          hasGoal: Boolean(m.hasGoal),
        }))
      : [],
  };

  return {
    weeklyRollGoals,
    productionGoals,
    ytdCompletionGoals,
    meta: {
      asOf: json?.meta?.asOf ?? null,
      requestedDate: json?.meta?.requestedDate ?? null,
      requestedDateInvalid: Boolean(json?.meta?.requestedDateInvalid),
    },
  };
}

// ─── Workflow Map ─────────────────────────────────────────────────────────────

/**
 * Fetch job-execution time windows + matching RTLS position data for the
 * Workflow Map feature.
 *
 * The backend (handleWorkflowMap in dataServe.js) automatically selects the
 * correct SQL path based on objectName:
 *
 *   objectName starts with "12WO"  →  Module path
 *     Queries RTLS_ModuleMetricsActivityHistory
 *     WHERE ProductionOrder = objectName AND OperationNumber = jobNumber
 *     AND job window overlaps [beginDateTime, endDateTime]
 *
 *   everything else               →  Personnel path
 *     Reformats "First Last" → "Last, First" (double-last-name safe)
 *     Queries RTLS_PersonnelMetricsActivityHist
 *     WHERE EmployeeName = formatted AND Description = jobName
 *     AND CAST(EventDate AS DATE) IN [beginDateTime date, endDateTime date]
 *
 * Date filtering is enforced directly in SQL WHERE clauses using
 * beginDateTime / endDateTime so only records that genuinely overlap the
 * selected filter window are returned.  For module records where
 * RealizedEndDate IS NULL the backend falls back to RealizedStartDate +
 * ReportedHours to compute a synthetic end; rows with neither are skipped.
 *
 * After the SQL lookup the backend resolves the Kinexon object_id, fetches
 * /riot-api/position-history for the full range, and filters positions to
 * points that fall inside a SQL job execution window.
 *
 * ── Where this is called from ────────────────────────────────────────────────
 *
 *   1. Live map iframe (access point #1):
 *        objectName + jobName/jobNumber from rtlsFilter.js "workflow" mode
 *        beginTs/endTs/beginDateTime/endDateTime from the filter date range
 *
 *   2. Module Metrics page (access point #2):
 *        objectName = Production Order (e.g. "12WO010404")
 *        jobNumber  = OperationNumber (e.g. 135)
 *        jobName    = informational label from the metrics row
 *        beginDateTime/endDateTime spanning the module's date range
 *
 *   3. Personnel Metrics page (access point #3):
 *        objectName = employee's display name (e.g. "Antonio Edwards")
 *        jobName    = activity Description from the metrics row
 *        beginDateTime/endDateTime spanning the employee's date range
 *
 * ── Parameters ───────────────────────────────────────────────────────────────
 *
 * @param {{
 *   objectName:      string,         RTLS object name (live list / module WO / employee name)
 *   jobName:         string,         Personnel: Description col; Module: for display only
 *   jobNumber?:      number | null,  Module path only: OperationNumber (required when 12WO)
 *   beginTs:         number,         Range start, Unix seconds (used by position-history fetch)
 *   endTs:           number,         Range end, Unix seconds (used by position-history fetch)
 *   beginDateTime:   string,         Range start as "YYYY-MM-DDTHH:MM" (used by SQL WHERE)
 *   endDateTime:     string,         Range end as "YYYY-MM-DDTHH:MM" (used by SQL WHERE)
 *   samplingPeriod?: number,         Seconds between position samples (default 30)
 * }} params
 *
 * @param {AbortSignal} [signal]
 *
 * ── Response shape (200) ─────────────────────────────────────────────────────
 *
 * @returns {Promise<{
 *   objectName:    string,
 *   jobName:       string,
 *   jobNumber:     number | null,
 *   path:          "module" | "personnel",
 *   rtlsObjectId:  number | null,        null = object not found in Kinexon
 *   windows: Array<{                     job execution time windows from SQL
 *     start:  number,                    Unix seconds (from SQL start date+time)
 *     end:    number,                    Unix seconds (from SQL end, or start + ReportedHours)
 *     raw: {                             untouched SQL column values
 *       startDate?:      string | null,  ISO-8601 (module path)
 *       startTime?:      string | null,  "HH:MM AM/PM"
 *       endDate?:        string | null,  ISO-8601 (module path); null = end was derived
 *       endTime?:        string | null,  "HH:MM AM/PM"; null = end was derived
 *       jobStatus?:      string | null,  "Ended" | "Started" | "Interrupted" (module path)
 *       reportedHours?:  number | null,  used when endDate IS NULL (module path)
 *       eventDate?:      string | null,  ISO-8601 (personnel path)
 *     },
 *   }>,
 *   }>,
 *   positions:     Array<[number, number, number]>,  [x, y, z] coords inside a window
 *   positionCount: number,
 *   windowCount:   number,
 *   samplingPeriod: number,
 *   meta: {
 *     beginTs:          number,
 *     endTs:            number,
 *     elapsedMs:        number,
 *     objectResolved:   boolean,
 *     windowsFromSql:   number,          total SQL rows before clamping
 *     rawPositionCount: number,          position points before window filter
 *   },
 * }>}
 *
 * @throws {DataReceiverError}
 *   .status 400 - missing / invalid parameters (caught client-side before network)
 *   .status 502 - upstream RTLS API unreachable
 *   .status 500 - SQL pool / query failure on the backend
 */
export async function fetchWorkflowMap(
  {
    objectName,
    jobName,
    jobNumber = null,
    beginTs,
    endTs,
    beginDateTime,
    endDateTime,
    samplingPeriod = 30,
  },
  signal,
) {
  // ── Client-side validation ─────────────────────────────────────────────────
  //
  // Mirrors the backend's validation so callers get immediate, descriptive
  // errors without a network round-trip when inputs are obviously wrong.

  if (!objectName || typeof objectName !== "string" || !objectName.trim()) {
    throw new DataReceiverError(
      "fetchWorkflowMap: 'objectName' is required.",
      400,
      null,
    );
  }

  if (!jobName || typeof jobName !== "string" || !jobName.trim()) {
    throw new DataReceiverError(
      "fetchWorkflowMap: 'jobName' is required.",
      400,
      null,
    );
  }

  if (!Number.isFinite(beginTs) || !Number.isFinite(endTs)) {
    throw new DataReceiverError(
      "fetchWorkflowMap: 'beginTs' and 'endTs' must be finite numbers (Unix seconds).",
      400,
      null,
    );
  }

  if (beginTs >= endTs) {
    throw new DataReceiverError(
      `fetchWorkflowMap: 'beginTs' (${beginTs}) must be earlier than 'endTs' (${endTs}).`,
      400,
      null,
    );
  }

  // beginDateTime / endDateTime are required for SQL date filtering.
  // Both must be non-empty strings in "YYYY-MM-DDTHH:MM" format.
  // Access points #2 and #3 (metrics pages) must derive these from whatever
  // date range they pass; the simplest conversion is new Date(ts * 1000).toISOString().slice(0,16).
  if (
    !beginDateTime ||
    typeof beginDateTime !== "string" ||
    !beginDateTime.trim()
  ) {
    throw new DataReceiverError(
      "fetchWorkflowMap: 'beginDateTime' is required (format: YYYY-MM-DDTHH:MM).",
      400,
      null,
    );
  }
  if (!endDateTime || typeof endDateTime !== "string" || !endDateTime.trim()) {
    throw new DataReceiverError(
      "fetchWorkflowMap: 'endDateTime' is required (format: YYYY-MM-DDTHH:MM).",
      400,
      null,
    );
  }

  // Module path requires a numeric jobNumber
  const isModulePath = objectName.trim().toUpperCase().startsWith("12WO");
  if (isModulePath && !Number.isFinite(Number(jobNumber))) {
    throw new DataReceiverError(
      "fetchWorkflowMap: 'jobNumber' must be a valid integer when objectName " +
        `starts with '12WO' (module path). Received: ${jobNumber}`,
      400,
      null,
    );
  }

  // ── Build query params and call backend ────────────────────────────────────

  const params = new URLSearchParams({
    objectName: objectName.trim(),
    jobName: jobName.trim(),
    beginTs: String(beginTs),
    endTs: String(endTs),
    // Sent as-is; SQL Server accepts "YYYY-MM-DDTHH:MM" via CAST(… AS DATETIME)
    beginDateTime: beginDateTime.trim(),
    endDateTime: endDateTime.trim(),
    samplingPeriod: String(Math.max(5, Number(samplingPeriod) || 30)),
  });

  // jobNumber is only meaningful on the module path but harmless to send always
  if (jobNumber != null) {
    params.set("jobNumber", String(jobNumber));
  }

  const json = await apimGet("workflow-map", params, signal);

  // ── Normalise response ─────────────────────────────────────────────────────
  //
  // Defensively coerce every field so consuming hooks never have to guard
  // against a partial 200 (which can occur if the SQL query succeeded but
  // position fetch partially failed).

  const windows = Array.isArray(json?.windows)
    ? json.windows.map((w) => ({
        start: Number(w.start ?? 0),
        end: Number(w.end ?? 0),
        raw: {
          // Module fields
          startDate: w.raw?.startDate ?? null,
          startTime: w.raw?.startTime ?? null,
          endDate: w.raw?.endDate ?? null,
          endTime: w.raw?.endTime ?? null,
          jobStatus: w.raw?.jobStatus ?? null,
          // reportedHours is surfaced when endDate was NULL (end was derived)
          reportedHours:
            w.raw?.reportedHours != null ? Number(w.raw.reportedHours) : null,
          // Personnel fields
          eventDate: w.raw?.eventDate ?? null,
        },
      }))
    : [];

  const positions = Array.isArray(json?.positions)
    ? json.positions.map(([x, y, z]) => [Number(x), Number(y), Number(z)])
    : [];

  return {
    objectName: String(json?.objectName ?? objectName),
    jobName: String(json?.jobName ?? jobName),
    jobNumber: json?.jobNumber != null ? Number(json.jobNumber) : null,
    path: json?.path === "module" ? "module" : "personnel",
    rtlsObjectId: json?.rtlsObjectId != null ? Number(json.rtlsObjectId) : null,
    windows,
    positions,
    positionCount: positions.length,
    windowCount: windows.length,
    samplingPeriod: Number(json?.samplingPeriod ?? samplingPeriod),
    meta: {
      beginTs: Number(json?.meta?.beginTs ?? beginTs),
      endTs: Number(json?.meta?.endTs ?? endTs),
      elapsedMs: Number(json?.meta?.elapsedMs ?? 0),
      objectResolved: Boolean(json?.meta?.objectResolved),
      windowsFromSql: Number(json?.meta?.windowsFromSql ?? 0),
      rawPositionCount: Number(json?.meta?.rawPositionCount ?? 0),
    },
  };
}

// ─── Avg Count (7-business-day averages) ─────────────────────────────────────

/**
 * Fetch 7-business-day averages for the four snapshot metrics stored in
 * RTLS_avgCounts (onlineMods, activePersonnel, noncompliances) and the
 * RollDuration average from RTLS_modRollTracker (modRolls).
 *
 * No parameters required — the backend derives the window (last 14 calendar
 * days, Mon–Fri only, today excluded) automatically.
 *
 * Consumed by: useAvgCount.js → HomePage metric cards (7-day comparison row)
 *
 * ── Data sources ──────────────────────────────────────────────────────────────
 *
 *   onlineMods       AVG(EntryCount) WHERE AvgType = 'onlineMods'
 *                    Written by the avg-count POST route (dataPost.js).
 *
 *   activePersonnel  AVG(EntryCount) WHERE AvgType = 'activePersonnel'
 *                    Written by the avg-count POST route (dataPost.js).
 *
 *   noncompliances   AVG(EntryCount) WHERE AvgType = 'noncompliances'
 *                    Written by the avg-count POST route (dataPost.js).
 *
 *   modRolls         AVG(RollDuration) from RTLS_modRollTracker — the stored
 *                    per-roll duration in minutes, averaged across all Mon–Fri
 *                    roll events in the window. null when no qualifying rows
 *                    exist (e.g. no rolls recorded on business days yet).
 *
 * ── Null semantics ────────────────────────────────────────────────────────────
 *
 *   Every metric can legitimately be null when the backend has no qualifying
 *   rows in the 14-day window (e.g. the POST cron hasn't run yet, or
 *   RTLS_modRollTracker has no Mon–Fri rows). Null is preserved as-is so
 *   the UI can render "—" rather than a misleading 0 or NaN delta.
 *
 * ── Response shape (200) ─────────────────────────────────────────────────────
 *
 *   {
 *     onlineMods:      number | null,   // avg EntryCount, 1 d.p.
 *     activePersonnel: number | null,
 *     noncompliances:  number | null,
 *     modRolls:        number | null,   // avg RollDuration (minutes), 1 d.p.
 *     meta: {
 *       windowStart: string,            // ISO-8601 UTC — inclusive lower bound
 *       windowEnd:   string,            // ISO-8601 UTC — inclusive upper bound (end of yesterday)
 *       asOf:        string,            // ISO-8601 UTC — query execution time
 *     }
 *   }
 *
 * @param {AbortSignal} [signal]
 *
 * @returns {Promise<{
 *   onlineMods:      number | null,
 *   activePersonnel: number | null,
 *   noncompliances:  number | null,
 *   modRolls:        number | null,
 *   meta: { windowStart: string, windowEnd: string, asOf: string },
 * }>}
 *
 * @throws {DataReceiverError}
 */
export async function fetchAvgCount(signal) {
  const json = await apimGet("avg-count", new URLSearchParams(), signal);

  // ── Shared null-safe numeric normaliser ───────────────────────────────────
  //
  // Preserves null as-is (meaning "no data in window") rather than coercing
  // to 0, which would produce misleading delta percentages in the UI.
  // All four metrics use the same rule: null → null, anything else → Number().

  function normAvg(raw) {
    return raw != null ? Number(raw) : null;
  }

  const onlineMods = normAvg(json?.onlineMods);
  const activePersonnel = normAvg(json?.activePersonnel);
  const noncompliances = normAvg(json?.noncompliances);
  const modRolls = normAvg(json?.modRolls);

  // ── meta ──────────────────────────────────────────────────────────────────
  const meta = {
    windowStart: json?.meta?.windowStart ?? null,
    windowEnd: json?.meta?.windowEnd ?? null,
    asOf: json?.meta?.asOf ?? null,
  };

  return { onlineMods, activePersonnel, noncompliances, modRolls, meta };
}

// ─── COMPARISON TOOL — CORE FETCH FUNCTION ───────────────────────────────────

/**
 * fetchComparison — fetches data for one comparison tab from dataServe.js.
 *
 * This is the single function used by useComparisonTool.js for all four modes.
 * The per-mode convenience exports below (fetchComparisonPeer etc.) wrap it
 * with the mode already bound — useful when calling a specific mode directly
 * outside the hook.
 *
 * @param  {string}       woNum   Work order number of the module being compared.
 * @param  {"peer"|"project"|"schedule"|"quality"|"jobs"} mode  Comparison mode.
 * @param  {AbortSignal}  [signal]  Optional AbortSignal from the hook's
 *                                  AbortController.  Pass undefined if no
 *                                  cancellation is needed.
 * @returns {Promise<{ data: object|null, error: ErrorObj|null }>}
 *
 * Success: data is the full response payload from dataServe (see route
 *          contract in dataServe.js for the shape per mode).
 * Cancelled (signal aborted by caller): { data: null, error: null }
 * Timed out (internal timer): { data: null, error: { code: "timeout", … } }
 * All other failures: { data: null, error: <ErrorObj> }
 */

// ─── SHARED INTERNAL HELPERS ──────────────────────────────────────────────────
/**
 * Per-request timeout for comparison fetches.
 * The peer and quality modes run the most queries in parallel; 20 s provides
 * margin without leaving the user waiting indefinitely.
 */
const COMPARISON_TIMEOUT_MS = 20_000;

/** Valid mode strings — must match the VALID_MODES array in dataServe.js. */
const VALID_MODES = ["peer", "project", "schedule", "quality", "jobs"];

/**
 * Machine-readable error codes returned in ErrorObj.code.
 * The hook and UI components switch on these to decide how to render.
 */
const ERROR_CODES = {
  NETWORK: "network_error", // fetch() itself threw (no connection, DNS)
  TIMEOUT: "timeout", // internal timer fired before response arrived
  HTTP: "http_error", // non-2xx, body not parseable as JSON
  API: "api_error", // non-2xx with JSON body (server error code)
  NOT_FOUND: "not_found", // HTTP 404 — module not in currentOnLineMods
  SERVER: "server_error", // HTTP 5xx
  PARSE: "parse_error", // 2xx but body is not valid JSON
  INVALID_ARG: "invalid_argument", // bad woNum / mode passed by caller
};

/**
 * buildComparisonUrl — constructs the full query string URL for a given mode.
 *
 * @param  {string}      woNum       Work order number, e.g. "12WO010799"
 * @param  {string}      mode        One of VALID_MODES
 * @param  {string}      [peerWoNum] Peer work order for head-to-head modes.
 *                                   Optional for mode="schedule" (backend
 *                                   auto-selects a peer when omitted).
 *                                   Required for mode="jobs" (the backend
 *                                   returns 400 if it is missing — see
 *                                   fetchComparisonJobs below).
 *                                   Ignored for "peer"/"project"/"quality".
 * @returns {string}  Fully-qualified URL ready for fetch()
 */
function buildComparisonUrl(woNum, mode, peerWoNum) {
  const params = new URLSearchParams({
    route: "module-comparison",
    woNum,
    mode,
  });
  // peerWoNum only matters for the head-to-head modes ("schedule", "jobs")
  // but adding it unconditionally is harmless — the backend ignores it for
  // every other mode.  Omitting it when empty keeps the URL clean.
  if (peerWoNum) params.set("peerWoNum", peerWoNum);

  // Every route in dataServe.js is dispatched through the single
  // "dataServe" Azure Function (app.http("dataServe", ...)), so the path
  // must include /dataServe — same as apimGet() does for every other route.
  // Without it, APIM has no matching operation and 404s before the request
  // ever reaches the function (nothing shows up in its logs either).
  return `${APIM_BASE_URL}/dataServe?${params.toString()}`;
}

/**
 * normalizeError — converts any caught error or HTTP failure into a consistent
 * ErrorObj that the hook and UI can safely consume.
 *
 * @param  {unknown}      thrown      The caught value from try/catch (may not be Error)
 * @param  {Response|null} response   The fetch Response if we got one, else null
 * @param  {object|null}  body        Parsed JSON body if available, else null
 * @returns {{ code, status, message, detail }}
 */
function normalizeError(thrown, response = null, body = null) {
  const status = response?.status ?? null;

  // API returned a structured error body (our jsonResponse 4xx/5xx format)
  if (body?.error) {
    const code =
      status === 404
        ? ERROR_CODES.NOT_FOUND
        : status >= 500
          ? ERROR_CODES.SERVER
          : ERROR_CODES.API;
    return {
      code,
      status,
      message: body.message ?? `Server returned ${status}.`,
      detail: body.detail ?? null,
    };
  }

  // HTTP error with no parseable body
  if (status != null && !response.ok) {
    return {
      code: status === 404 ? ERROR_CODES.NOT_FOUND : ERROR_CODES.HTTP,
      status,
      message: `Request failed with status ${status}.`,
      detail: null,
    };
  }

  // JSON parse failure (response was 2xx but body was not valid JSON)
  if (thrown?.name === "SyntaxError") {
    return {
      code: ERROR_CODES.PARSE,
      status,
      message: "Server returned an unrecognised response format.",
      detail: thrown.message,
    };
  }

  // Network failure — fetch() threw before a response arrived
  if (thrown?.name === "TypeError") {
    return {
      code: ERROR_CODES.NETWORK,
      status: null,
      message: "Could not reach the data server. Check your connection.",
      detail: thrown.message,
    };
  }

  // Catch-all
  return {
    code: ERROR_CODES.HTTP,
    status,
    message: thrown?.message ?? "An unknown error occurred.",
    detail: null,
  };
}

/**
 * fetchComparison — fetches data for one comparison tab from dataServe.js.
 *
 * This is the single function used by useComparisonTool.js for all five modes.
 * The per-mode convenience exports below (fetchComparisonPeer etc.) wrap it
 * with the mode already bound — useful when calling a specific mode directly
 * outside the hook.
 *
 * @param  {string}       woNum      Work order number of the module being compared.
 * @param  {"peer"|"project"|"schedule"|"quality"|"jobs"} mode  Comparison mode.
 * @param  {AbortSignal}  [signal]   Optional AbortSignal from the hook's
 *                                   AbortController.  Pass undefined if no
 *                                   cancellation is needed.
 * @param  {string}       [peerWoNum]  Peer work order for head-to-head comparison.
 *                                     mode="schedule": optional — the backend
 *                                       auto-selects the closest peer by
 *                                       completion rate when omitted.
 *                                     mode="jobs": required — the backend
 *                                       returns a 400 invalid_argument-style
 *                                       error (surfaced here as ERROR_CODES.API)
 *                                       if it is missing. Callers should pass
 *                                       the peerWoNum already resolved by a
 *                                       prior mode="schedule" fetch.
 * @returns {Promise<{ data: object|null, error: ErrorObj|null }>}
 *
 * Success: data is the full response payload from dataServe (see route
 *          contract in dataServe.js for the shape per mode).
 * Cancelled (signal aborted by caller): { data: null, error: null }
 * Timed out (internal timer): { data: null, error: { code: "timeout", … } }
 * All other failures: { data: null, error: <ErrorObj> }
 */
export async function fetchComparison(woNum, mode, signal, peerWoNum) {
  // ── Guard: validate arguments before touching the network ─────────────────
  if (!woNum || typeof woNum !== "string" || !woNum.trim()) {
    return {
      data: null,
      error: {
        code: ERROR_CODES.INVALID_ARG,
        status: null,
        message: "fetchComparison: woNum must be a non-empty string.",
        detail: null,
      },
    };
  }

  if (!VALID_MODES.includes(mode)) {
    return {
      data: null,
      error: {
        code: ERROR_CODES.INVALID_ARG,
        status: null,
        message: `fetchComparison: mode "${mode}" is not valid. Use one of: ${VALID_MODES.join(", ")}.`,
        detail: null,
      },
    };
  }

  // ── Guard: don't start if the caller already aborted ──────────────────────
  if (signal?.aborted) {
    return { data: null, error: null };
  }

  // ── Set up internal AbortController for timeout ────────────────────────────
  // We create our own controller so we can independently time out the request
  // without the caller needing to manage the timer.
  // The caller's signal is forwarded: if it fires, we abort our controller so
  // the underlying fetch() sees exactly one abort signal regardless of source.
  const internalController = new AbortController();
  const internalSignal = internalController.signal;

  // Forward caller abort → our controller.
  // { once: true } ensures the listener is automatically removed after firing.
  const onCallerAbort = () => internalController.abort(signal?.reason);
  signal?.addEventListener("abort", onCallerAbort, { once: true });

  // Internal timeout — aborts with a distinguishable reason object.
  const timeoutId = setTimeout(
    () => internalController.abort(new Error("COMPARISON_TIMEOUT")),
    COMPARISON_TIMEOUT_MS,
  );

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const url = buildComparisonUrl(woNum.trim(), mode, peerWoNum?.trim());
  let response = null;
  let body = null;

  try {
    response = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal: internalSignal,
    });

    // Always attempt to parse JSON even on error responses — the server sends
    // structured { error, message, detail } bodies for 4xx/5xx.
    body = await response.json();
  } catch (err) {
    // ── Abort handling ───────────────────────────────────────────────────────
    if (err.name === "AbortError") {
      // Was this aborted by the CALLER (intentional) or by our TIMEOUT?
      // Check the internal controller's abort reason — if it contains our
      // sentinel string the timeout fired; otherwise the caller aborted.
      const reason = internalSignal.reason;
      const isTimeout =
        reason instanceof Error && reason.message === "COMPARISON_TIMEOUT";

      if (isTimeout) {
        return {
          data: null,
          error: {
            code: ERROR_CODES.TIMEOUT,
            status: null,
            message:
              `Comparison request timed out after ${COMPARISON_TIMEOUT_MS / 1000} s. ` +
              "The quality and jobs modes run multiple queries and can be slow " +
              "on large datasets. Try again or contact your administrator.",
            detail: null,
          },
        };
      }

      // Caller aborted (tab switched, modal closed, component unmounted) —
      // this is not an error condition; the hook will ignore the result.
      return { data: null, error: null };
    }

    // Any other thrown error (network failure, JSON SyntaxError …)
    return { data: null, error: normalizeError(err, response, body) };
  } finally {
    // Always clean up to prevent memory leaks.
    clearTimeout(timeoutId);
    signal?.removeEventListener("abort", onCallerAbort);
  }

  // ── HTTP-level error ───────────────────────────────────────────────────────
  if (!response.ok) {
    return { data: null, error: normalizeError(null, response, body) };
  }

  // ── Sanity-check: confirm the server echoed the expected mode ──────────────
  // dataServe.js always includes { mode } in its 200 response.  A mismatch
  // would indicate a caching or proxy issue serving a stale response.
  if (body?.mode && body.mode !== mode) {
    console.warn(
      `[dataReceiver] fetchComparison: expected mode "${mode}" ` +
        `but server returned mode "${body.mode}". Discarding response.`,
    );
    return {
      data: null,
      error: {
        code: ERROR_CODES.PARSE,
        status: response.status,
        message: "Server returned data for the wrong comparison mode.",
        detail: `Expected "${mode}", received "${body.mode}".`,
      },
    };
  }

  // ── Success ────────────────────────────────────────────────────────────────
  return { data: body, error: null };
}
// ─── COMPARISON TOOL — PER-MODE CONVENIENCE EXPORTS ──────────────────────────
//
// These thin wrappers bind the `mode` argument so call sites that only ever
// use one mode can import a named function instead of passing the mode string.
// The hook (useComparisonTool.js) uses fetchComparison() directly because it
// dispatches all four modes from a single function.
//
// All four share the same base signature:
//   (woNum: string, signal?: AbortSignal) → Promise<{ data, error }>
// fetchComparisonSchedule adds an optional peerWoNum parameter:
//   (woNum: string, signal?: AbortSignal, peerWoNum?: string) → Promise<…>

/**
 * fetchComparisonPeer — Item Number Comparison tab data.
 * Returns peers sharing LEFT(ItemNum, 7) type class + peer averages.
 *
 * @param  {string}      woNum
 * @param  {AbortSignal} [signal]
 * @returns {Promise<{ data: ComparisonPeerPayload|null, error: ErrorObj|null }>}
 */
export function fetchComparisonPeer(woNum, signal) {
  return fetchComparison(woNum, "peer", signal);
}

/**
 * fetchComparisonProject — Project Progress tab data.
 * Returns all modules for the same Property (scheduled, in-progress, completed).
 *
 * @param  {string}      woNum
 * @param  {AbortSignal} [signal]
 * @returns {Promise<{ data: ComparisonProjectPayload|null, error: ErrorObj|null }>}
 */
export function fetchComparisonProject(woNum, signal) {
  return fetchComparison(woNum, "project", signal);
}

/**
 * fetchComparisonSchedule — Head to Head Comparison tab data.
 * Returns full metric snapshots for the selected module and one chosen peer,
 * plus the list of available same-typeClass peers for the selector dropdown.
 *
 * When peerWoNum is omitted, the backend auto-selects the peer whose
 * completionRate is closest to the selected module's.
 *
 * @param  {string}      woNum
 * @param  {AbortSignal} [signal]
 * @param  {string}      [peerWoNum]  WO# of the peer to compare against.
 * @returns {Promise<{ data: ComparisonSchedulePayload|null, error: ErrorObj|null }>}
 */
export function fetchComparisonSchedule(woNum, signal, peerWoNum) {
  return fetchComparison(woNum, "schedule", signal, peerWoNum);
}

/**
 * fetchComparisonJobs — Job Completion Comparison tab data (the new slider
 * position alongside Head to Head's metrics view).
 *
 * Returns every job worked on each of the two modules (status, timing, hours,
 * assigned workers) plus a computed summary per module, so the UI can render
 * a job-by-job comparison rather than just aggregate metrics.
 *
 * peerWoNum IS REQUIRED — unlike fetchComparisonSchedule, this call does not
 * trigger backend peer auto-selection. Pass the peerWoNum that a prior
 * fetchComparisonSchedule() call already resolved (either the user's explicit
 * choice or the backend's auto-selected peer), so both slider positions
 * always compare the same pair of modules. Omitting it returns an
 * ERROR_CODES.API result with a 400 status from the backend.
 *
 * @param  {string}      woNum        Work order number of the module being compared.
 * @param  {string}      peerWoNum    WO# of the peer module (required, see above).
 * @param  {AbortSignal} [signal]
 * @returns {Promise<{ data: ComparisonJobsPayload|null, error: ErrorObj|null }>}
 */
export function fetchComparisonJobs(woNum, peerWoNum, signal) {
  return fetchComparison(woNum, "jobs", signal, peerWoNum);
}

/**
 * fetchComparisonQuality — Quality Analysis tab data.
 * Returns NC event log, reason breakdown, alert status, FPR vs goal,
 * and peer-average NC rate.  The excluded transit reason
 * ("Moving mod to next station finish work there") is filtered server-side.
 *
 * @param  {string}      woNum
 * @param  {AbortSignal} [signal]
 * @returns {Promise<{ data: ComparisonQualityPayload|null, error: ErrorObj|null }>}
 */
export function fetchComparisonQuality(woNum, signal) {
  return fetchComparison(woNum, "quality", signal);
}

// ─── PAYLOAD TYPE DOCUMENTATION ───────────────────────────────────────────────
//
// The shapes below document what .data contains on a successful fetch.
// These are plain-object contracts, not TypeScript types — keep them in sync
// with the jsonResponse() calls at the bottom of each handler in dataServe.js.
//
// ComparisonPeerPayload
// ─────────────────────
// {
//   mode:         "peer",
//   woNum:        string,
//   typeClass:    string,                         // LEFT(ItemNum, 7)
//   thisModule:   PeerModule | null,
//   peers: {
//     inProgress: PeerModule[],
//     completed:  CompletedPeerModule[],
//   },
//   peerAverages: {
//     hoursOnLine:           number | null,
//     completionRate:        number | null,
//     hoursPerSqft:          number | null,
//     ncRate:                number | null,
//     avgTotalDurationHours: number | null,
//   },
// }
//
// PeerModule fields: woNum, itemNum, property, sqft, actualOnlineDate,
//   scheduledOfflineDate, hoursOnLine, completionRate, totalMannedHours,
//   hoursPerSqft, jobsRedone, totalTasks
//
// CompletedPeerModule adds: actualOfflineDate, totalHoursOnLine,
//   scheduleVarianceDays  (drops: hoursOnLine, completionRate, jobsRedone, totalTasks)
//
// ComparisonProjectPayload
// ────────────────────────
// {
//   mode:     "project",
//   woNum:    string,
//   property: string,
//   totals:   { totalModules: number, totalSqft: number },
//   scheduled:   ScheduledModule[],
//   inProgress:  ActiveProjectModule[],  // merged with NC counts
//   completed:   CompletedProjectModule[],
//   avgNcPerModule: number | null,
// }
//
// ComparisonSchedulePayload  (HEAD TO HEAD — replaces the old schedule payload)
// ──────────────────────────
// {
//   mode:           "schedule",
//   woNum:          string,
//   typeClass:      string,
//   thisModule:     ModuleSnapshot,
//   peerModule:     ModuleSnapshot | null,   null when no same-typeClass peers exist
//   peerWoNum:      string | null,           the peer actually used (for the UI selector)
//   availablePeers: { woNum, itemNum, property, completionRate }[],
// }
//
// ModuleSnapshot fields (both thisModule and peerModule):
//   woNum, itemNum, property, sqft
//   actualOnlineDate, scheduledOfflineDate
//   hoursOnLine, completionRate, totalMannedHours, hoursPerSqft
//   jobsRedone, totalTasks
//   daysOnLine, daysRemaining
//
// ComparisonQualityPayload
// ────────────────────────
// {
//   mode:      "quality",
//   woNum:     string,
//   typeClass: string,
//   thisModule:       { itemNum, jobsRedone, totalTasks },
//   // stationSummary removed — Station NC Heatmap section dropped
//   ncDetail:         { stationId, zoneName, taskName, stepName, stepStatus,
//                       updatedBy, updatedAt, isAlertOpened, isAlertClosed,
//                       alertType, reasonText }[],
//                     // "Moving mod to next station finish work there" excluded
//   reasonBreakdown:  { reasonText, frequency }[],
//                     // same exclusion applied
//   alertStatus:      { openAlerts, closedAlerts },
//   fprGoal:          number | null,   // e.g. 1.0 = 100%
//   peerAvgNcRate:    number | null,   // percentage, e.g. 7.2
// }

// ComparisonJobsPayload  (Job Completion — new slider position on Head to Head)
// ─────────────────────
// {
//   mode:        "jobs",
//   woNum:       string,
//   peerWoNum:   string,
//   thisJobs:    JobRow[],
//   peerJobs:    JobRow[],
//   thisSummary: JobSummary,
//   peerSummary: JobSummary,
// }
//
// JobRow fields:
//   jobId, operationNumber, jobStatus,
//   startDate, startTime, endDate, endTime,   // dates are ISO-8601, times raw nvarchar e.g. "08:34 AM"
//   hours,                                    // number | null (ReportedHours)
//   workers: { employeeId: string|null, employeeName: string|null }[]
//
// JobSummary fields (computed server-side per module):
//   totalJobs, completedJobs, inProgressJobs, pendingJobs,
//   completionRate,      // percent, 1dp, 0 when totalJobs = 0
//   totalHours,          // sum of hours across completed jobs only
//   avgHoursPerJob,      // totalHours / completedJobs, 2dp, 0 when completedJobs = 0
//   uniqueWorkerCount    // distinct employeeId across all of this module's jobs

// ─── Available Modules — search index for Head to Head peer selection ─────────
//
// fetchAvailableModules() fetches the full union of online + completed modules
// from the available-modules route and returns it as a flat sorted array.
//
// CACHING STRATEGY
// ────────────────
// The list is O(hundreds) of rows and changes infrequently within a session
// (a module rolls off at most once per day; new modules come online once per
// day).  The caller (useAvailableMods) implements a module-level cache with a
// 5-minute TTL so repeated opens of the Head to Head tab don't re-fetch.
// This function itself is stateless — it always hits the network.
//
// RESPONSE CONTRACT
// ─────────────────
// dataServe returns:
//   { modules: [{ woNum, itemNum, batchNum }], count: number }
//
// This function normalises the envelope and returns the inner array directly.
// Each element is guaranteed to have at minimum a non-empty woNum; itemNum and
// batchNum may be null for edge-case YTD rows (see dataServe handler comments).
//
// ABORT BEHAVIOUR
// ───────────────
// signal is forwarded to apimGet unchanged.  On abort, apimGet throws an
// AbortError which propagates to the caller — useAvailableMods catches it by
// checking err.name === "AbortError" and does NOT update state.
//
// ERROR HANDLING
// ──────────────
// Any non-abort error is re-thrown as a DataReceiverError (thrown by apimGet).
// The hook's catch block writes it to its `error` state for the UI to display.
//
// @param {AbortSignal} [signal]
// @returns {Promise<Array<{ woNum: string, itemNum: string|null, batchNum: string|null }>>}
// @throws {DataReceiverError}  on HTTP error, non-JSON response, or network failure
//
export async function fetchAvailableModules(signal) {
  const json = await apimGet(
    "available-modules",
    new URLSearchParams(),
    signal,
  );

  // Normalise envelope: modules[] (current) → data[] (legacy) → bare array
  const raw = Array.isArray(json?.modules)
    ? json.modules
    : Array.isArray(json?.data)
      ? json.data
      : Array.isArray(json)
        ? json
        : [];

  // Guarantee shape: drop any row with no woNum (shouldn't occur, defensive)
  return raw
    .filter((r) => r?.woNum)
    .map((r) => ({
      woNum: String(r.woNum),
      itemNum: r.itemNum != null ? String(r.itemNum) : null,
      batchNum: r.batchNum != null ? String(r.batchNum) : null,
    }));
}

// ─── PAYLOAD TYPE DOCUMENTATION ───────────────────────────────────────────────
//
// AvailableModule
// ───────────────
// {
//   woNum:    string,         // "12WO010870"  — always present
//   itemNum:  string | null,  // "P24-PS-TG-110" — null for YTD-only rows
//   batchNum: string | null,  // "CDLRZ00208107&08002"
// }
//
// fetchAvailableModules return value:
//   AvailableModule[]   ordered by woNum DESC (most recent first)

// ─── Skeleton callers (stub - wire up as backend routes are implemented) ──────
//
// Uncomment and fill in params as each backend handler is promoted from
// skeleton → implemented in dataServe.js.

// export async function fetchHomepageMetrics(signal) {
//   return apimGet("homepage-metrics", new URLSearchParams(), signal);
// }

// export async function fetchProductionMilestones(signal) {
//   return apimGet("production-milestones", new URLSearchParams(), signal);
// }

// ... (add remaining route callers following the same pattern)
